/* =====================================================================
   THE ROOM ENGINE — E3, THE LETTERING

   Two halves, one file.

   PLACEMENT (worker-safe: no DOM at all) — `RoomLettering.place()` is
   called from field.js with the record and the finished field, and it
   works out WHERE every name on the plate goes, in map units, by
   survey-map.js's own rules (line numbers cited). It runs in the worker
   beside the field, so nothing here is main-thread work at load.

   DRAWING (main thread) — `RoomLettering.attach()` rasterises a glyph
   atlas at runtime from the page's own Sorts Mill Goudy (normal +
   italic), at a size chosen for the current band and re-rasterised when
   the band changes; `texImage2D(..., canvas)` takes the canvas straight,
   and the atlas is never read back (ROOM-PLAN §4: getImageData is up to
   half a second and a broken budget). The glyphs are laid out ONCE PER
   ZOOM — a pan does not move a letter relative to the ground — and drawn
   as one instanced quad each.

   R34 (a): THE NAMES ARE PRINTED ON THE PAPER. Until R34 each quad was
   built in DEVICE pixels about its anchor's projection, so a name was the
   same size on screen at every zoom and every tilt — the digital-map
   convention, and what `calc(Npx / var(--z))` buys on the sheet. This is a
   sheet of paper on a desk: type printed on paper gets bigger as the eye
   comes closer and foreshortens with the sheet when it is turned, and
   letters printed with a gap between them keep that gap. So the quad is
   four corners OF THE PAPER, in MAP units about the anchor and along the
   baseline's own map tangent — the compass rose's treatment (marks.js SPR
   mode 3, R5 d) given to every glyph. The size in map units is set so the
   name measures what the type table says at the FIT and grows with the
   zoom to a cap (GROW_CAP); `?letmap=0` is the pair, and restores the
   device-pixel quad exactly.

   The cartouche and the marginalia are NOT here: they come from the
   furniture texture E1 rasters from sheet-frame.svg.
   ===================================================================== */
(function (root) {
  'use strict';
  var LET = root.RoomLettering = root.RoomLettering || {};

  /* =================================================================
     THE TYPE — every size, tracking, colour and halo is the rule in
     styles.css for the class of the same name (ROOM-PLAN §4's table).
     `size` and `halo` are CSS pixels and are constant on screen; the
     sheet writes them `calc(Npx / var(--z))`, which is the same thing.
     `lod`: '' always, 'mid' from the mid band, 'near' from the near
     band, 'wide' until the near band — the sheet's own .lod- classes
     (styles.css:1166), faded over the octave E2 fades the contours on
     instead of switched at a boundary.
     ================================================================= */
  /* `far` — R11 (a): THE DISTANCE A NAME SURVIVES TO, AND THE PRIORITY.
     E3 fades a name by ZOOM octave; nothing faded one by DISTANCE, and at
     tilt 80 the whole far band still printed — seven names inside 250 px
     in the west (room-rev2/s-t80-y30.png: Path Dreads, Lago di Roma,
     RE:INVENT, OPUS 4.5 · 24 NOV 2025, Sizewell Reach, CHESTER, 2026).

     A name is a constant size on screen, so it never gets SMALL — but its
     glyphs are placed on the projection of its own baseline, so what the
     far band actually loses is GROUND: the same map units buy fewer pixels
     out there, the names are laid over each other, and they pile. The
     measure is therefore how many device pixels a map unit buys HERE
     against what it buys with the sheet flat — the shader takes a unit
     step along the tilt AXIS, which the tilt never foreshortens, so what
     is left is what the TILT and the perspective together take off the
     sheet's north-south run — which is exactly what collapses the rows
     into each other and piles the names.

     MEASURED on this record, over every label inside the window: the fit
     1.000 everywhere; tilt 55 yaw 0 0.493–0.638; tilt 55 yaw 45
     0.347–0.690; tilt 70 yaw 0 0.267–0.439; tilt 80 yaw 30 0.184–0.323.
     So `far` is that ratio's floor for the kind, faded over a quarter
     again, and it is the priority the item asks for: the sea, the marker
     horizon and TERRA to 0.13 (nothing in view reaches it), the reaches, the
     fjords and the ranges to 0.19, everything else to 0.225 — under the
     whole of tilt 80 and over the whole of tilt 55, so tilt 80's western
     pile goes and the tilt-55 sheet does not move by a pixel.

     The SQUASH is the second half and is shared: a baseline foreshortened
     under about a third of its flat length (the item's 35 %) has its
     letters on top of each other whatever the distance. Both are 1.0 with
     the sheet flat at every zoom, and uRelief guards them besides, so the
     flat picture cannot move. */
  /* ==== R34 (c): THE TYPE TABLE, SET FOR HIS EYE.
     Tommy at 1536x864 css on devicePixelRatio 1.75 (a 27" 1440p at about a
     metre): "all the writing on the map is too small to read ... the letters
     are too close and the spaces between the words too small". An 11 px
     italic at that distance is under four arc-minutes of x-height and is not
     type any more, it is texture. The sizes below are the item's table, and
     every one of them is now a size ON THE PAPER (R34 a): it is what the
     name measures at the FIT, and it grows with the sheet from there.

     THE TRACKING. The italic names were set at 0.04 em, which is the
     letterspacing of a caption and not of a name that has to be read across
     a sound: Goudy Italic's counters close up at that size and the word
     blurs ("the letters kinda blur into each other"). They are 0.08 em now;
     peakName, a proper name in mixed case rather than a run of water, 0.06.

     THE HALO. It is a dilation of the glyph's own coverage, so it eats the
     counters from OUTSIDE: the rule is a fifth of the x-height at each size
     — 0.09 x size for the lowercase kinds (Goudy's x-height is about 0.45
     em), 0.132 x size for the all-caps and figure kinds, whose "x-height" is
     the cap/figure height (about 0.66 em). Every halo below is at or under
     its own kind's ceiling; the figure in the comment is that ceiling.
     oceanName is the one size the item left open: 19 keeps the ocean over
     the sea (17) it is the arm of, which was the hierarchy at 17 over 15.
     ==== */
  /* TI1: the smallest TERRA INCOGNITA is set at to fit its paper, as a
     share of its own size; narrower paper than that and it is not set */
  var TERRA_FIT_MIN = 0.6;
  var KIND = {
    contourFig: { size: 9.5, it: 0, track: 0,    col: '#97562a', halo: 0,   lod: 'mid',  a: 1,    cut: 1, inPlate: 1, far: 0.225 },
    spot:       { size: 9.5, it: 0, track: 0,    col: '#2e261c', halo: 1.25,lod: '',     a: 1,    far: 0.225 },   /* ceiling 1.25 */
    seaName:    { size: 17,  it: 1, track: 0.30, col: '#4a7079', halo: 1.2, lod: '',     a: 0.8,  far: 0.13 },    /* 2.24 */
    oceanName:  { size: 19,  it: 0, track: 0.34, col: '#4a7079', halo: 1.5, lod: 'wide', a: 0.78, far: 0.13 },    /* 2.51 */
    waterName:  { size: 13.5,it: 1, track: 0.08, col: '#4a7079', halo: 1.2, lod: '',     a: 1,    far: 0.19 },    /* 1.21 */
    bight:      { size: 11,  it: 1, track: 0.08, col: '#4a7079', halo: 0.95,lod: 'near', a: 1,    far: 0.225 },   /* 0.99 */
    calm:       { size: 11,  it: 1, track: 0.08, col: '#4a7079', halo: 0.95,lod: 'near', a: 1,    far: 0.225 },   /* 0.99 */
    trackName:  { size: 10,  it: 0, track: 0.04, col: '#55483a', halo: 1.1, lod: '',     a: 1,    far: 0.225 },   /* 1.32 */
    rangeName:  { size: 16,  it: 0, track: 0.04, col: '#9a6b41', halo: 1.4, lod: '',     a: 0.85, far: 0.19 },    /* 2.11 */
    peakName:   { size: 12,  it: 1, track: 0.06, col: '#55483a', halo: 1.05,lod: '',     a: 1,    far: 0.225 },   /* 1.08 */
    /* R36: the marker horizon's name. The fault's plum (#5c2f4a) at the
       fault's weight was a FEATURE of a thing that is not there any more;
       a horizon is a graticule line, so its name takes the graticule's own
       ink — the year figure's sepia — and keeps the fault's priority
       (far: 0.13: under the sea names, over the reaches). */
    horizonLabel:{ size: 10,  it: 0, track: 0.04, col: '#6a4d2c', halo: 1.4, lod: '',     a: 1,    far: 0.13 },    /* 1.32 */
    terra:      { size: 20,  it: 0, track: 0.10, col: '#9c8455', halo: 0,   lod: '',     a: 1,    far: 0.13 },
    /* R37 (a): `mark` — this is a SYMBOL, not a name. The spot's dot is
       set at the same anchor as its own figure, a few screen pixels under
       it, so a screen test that did not know the difference faded every
       spot height's number behind its own dot (measured at the fit: four
       figures gone, 945 device px, room-rev6/R37-spotdot-*.png). A mark
       neither blocks a name nor is faded by one. */
    spotDot:    { size: 9.5, it: 0, track: 0,    col: '#2e261c', halo: 1.1, lod: '',     a: 1,    far: 0.225, mark: 1 },
    yearFig:    { size: 12,  it: 0, track: 0.10, col: '#6a4d2c', halo: 1.5, lod: '',     a: 1,    far: 0.225 }    /* 1.58 */
  };
  LETKIND(KIND);
  function LETKIND(K) { for (var k in K) if (K[k].col) K[k].rgb = hex(K[k].col); }
  function hex(h) {
    return [parseInt(h.slice(1, 3), 16) / 255, parseInt(h.slice(3, 5), 16) / 255, parseInt(h.slice(5, 7), 16) / 255];
  }

  /* The advance of every glyph of Sorts Mill Goudy, in ems, MEASURED in
     this browser (the figures are the LN face's lining figures: tabular,
     0.527 em roman / 0.508 italic, from the font's own hmtx), MEASURED in
     this browser from the page's own woff2 (both styles, at 200 px, to
     four places). The placement runs in the WORKER, which has no canvas
     and so cannot measure: it has to reserve ground at the widths the
     drawing will use, and 8102's own fallback when there is no canvas —
     0.46 em a letter — is wide enough to move a name. `attach()` checks
     this table against measureText each time it cuts the atlas and
     reports the largest disagreement as `advMaxErr`. */
  var GOUDY = { r: {
    "0":0.527,"1":0.527,"2":0.527,"3":0.527,"4":0.527,"5":0.527,"6":0.527,"7":0.527,"8":0.527,
    "9":0.527," ":0.213,"!":0.276,"\"":0.459,"#":0.533,"$":0.533,"%":0.771,"&":0.947,"'":0.264,
    "(":0.326,")":0.326,"*":0.431,"+":0.527,",":0.25,"-":0.34,".":0.252,"/":0.416,":":0.26,
    ";":0.262,"<":0.527,"=":0.527,">":0.527,"?":0.35,"@":0.821,"A":0.812,"B":0.63,"C":0.807,
    "D":0.792,"E":0.604,"F":0.554,"G":0.818,"H":0.791,"I":0.328,"J":0.29,"K":0.724,"L":0.603,
    "M":0.919,"N":0.798,"O":0.853,"P":0.564,"Q":0.839,"R":0.718,"S":0.596,"T":0.703,"U":0.815,
    "V":0.807,"W":1.11,"X":0.751,"Y":0.712,"Z":0.563,"[":0.287,"\\":0.416,"]":0.287,"^":0.333,
    "_":0.213,"`":0.439,"a":0.461,"b":0.547,"c":0.452,"d":0.565,"e":0.466,"f":0.345,"g":0.488,
    "h":0.587,"i":0.284,"j":0.281,"k":0.545,"l":0.279,"m":0.827,"n":0.591,"o":0.547,"p":0.555,
    "q":0.559,"r":0.375,"s":0.393,"t":0.342,"u":0.576,"v":0.523,"w":0.748,"x":0.548,"y":0.498,
    "z":0.378,"{":0.273,"|":0.209,"}":0.273,"~":0.38,"\u00a0":0.213,"\u2009":0.166,
    "\u2018":0.264,"\u2019":0.264,"\u201c":0.459,"\u201d":0.459,"\u2013":0.5,"\u2014":1,
    "\u00b7":0.252,"\u00b0":0.259,"\u00e9":0.466,"\u00ed":0.284,"\u00e1":0.461,"\u00fa":0.576,
    "\u00f3":0.547,"\u00e8":0.466,"\u00e0":0.461,"\u00f6":0.547,"\u00e4":0.461,"\u00fc":0.576,
    "\u00f1":0.591,"\u00e7":0.452,"\u00c9":0.604,"\u00cd":0.328,"\u00a3":0.557,"\u2020":0.5
  }, i: {
    "0":0.508,"1":0.508,"2":0.508,"3":0.508,"4":0.508,"5":0.508,"6":0.508,"7":0.508,"8":0.508,
    "9":0.508," ":0.213,"!":0.249,"\"":0.41,"#":0.533,"$":0.488,"%":0.771,"&":0.834,"'":0.239,
    "(":0.288,")":0.288,"*":0.431,"+":0.508,",":0.226,"-":0.282,".":0.222,"/":0.416,":":0.298,
    ";":0.298,"<":0.508,"=":0.508,">":0.508,"?":0.413,"@":0.821,"A":0.771,"B":0.612,"C":0.762,
    "D":0.767,"E":0.631,"F":0.585,"G":0.773,"H":0.786,"I":0.325,"J":0.405,"K":0.774,"L":0.589,
    "M":0.911,"N":0.804,"O":0.815,"P":0.576,"Q":0.807,"R":0.637,"S":0.481,"T":0.692,"U":0.827,
    "V":0.78,"W":1.053,"X":0.737,"Y":0.69,"Z":0.587,"[":0.266,"\\":0.416,"]":0.266,"^":0.333,
    "_":0.213,"`":0.439,"a":0.518,"b":0.471,"c":0.418,"d":0.495,"e":0.402,"f":0.302,"g":0.435,
    "h":0.485,"i":0.25,"j":0.242,"k":0.452,"l":0.261,"m":0.762,"n":0.526,"o":0.446,"p":0.488,
    "q":0.507,"r":0.352,"s":0.333,"t":0.299,"u":0.525,"v":0.427,"w":0.675,"x":0.475,"y":0.407,
    "z":0.409,"{":0.266,"|":0.209,"}":0.266,"~":0.38,"\u00a0":0.213,"\u2009":0.166,
    "\u2018":0.239,"\u2019":0.239,"\u201c":0.41,"\u201d":0.41,"\u2013":0.5,"\u2014":1,
    "\u00b7":0.301,"\u00b0":0.259,"\u00e9":0.402,"\u00ed":0.25,"\u00e1":0.518,"\u00fa":0.525,
    "\u00f3":0.446,"\u00e8":0.402,"\u00e0":0.518,"\u00f6":0.446,"\u00e4":0.518,"\u00fc":0.525,
    "\u00f1":0.526,"\u00e7":0.418,"\u00c9":0.631,"\u00cd":0.325,"\u00a3":0.53,"\u2020":0.5
  } };
  var ADV = root.__GOUDY_ADV || GOUDY;

  var SPACE_FALLBACK = 0.46;
  function advOf(ch, it) {
    var t = ADV && ADV[it ? 'i' : 'r'];
    var v = t && t[ch];
    return (v === undefined || v === null) ? SPACE_FALLBACK : v;
  }
  function inkEm(s, it) { var e = 0; for (var i = 0; i < s.length; i++) e += advOf(s[i], it); return e; }
  function inkWidth(s, size, it, track) { return (inkEm(s, it) + (track || 0) * Math.max(0, s.length - 1)) * size; }
  /* ==== R34 (c): THE WORD SPACE IN LETTERSPACED CAPS.
     `spaced()` used to put its gap character between EVERY pair of
     characters of the string, the one that was already a space included, so
     a word break came out as gap + space + gap. At oceanName's 0.34 em
     tracking over a thin space that is a letter gap of 0.846 em against a
     word gap of 1.905 — a ratio of 2.25 — and MARE OTIOSUM reads as one run
     of capitals: Tommy's "the spaces between the words too small".

     The gap between two letters set k gap-characters apart is
     (k + 1) x track + k x adv(gap), in ems, because the pen takes each
     glyph's advance PLUS the tracking. So the word break is SOLVED rather
     than guessed: the smallest k whose gap is at least 2.6x the letter gap
     (the item's floor is 2.5; the extra tenth is headroom against the
     advance table). The letter gap itself is the caller's own character, so
     a name set on thin spaces keeps them. `kind` names the row of KIND the
     string will be set in, so the tracking solved against is the one the
     name is actually set at. ==== */
  function wordSpaces(track, gapEm) {
    var letter = 2 * track + gapEm;
    for (var k = 1; k < 9; k++) if ((k + 1) * track + k * gapEm >= 2.6 * letter) return k;
    return 9;
  }
  function spaced(s, kind, gap) {
    if (gap === undefined) gap = ' ';
    var tr = (KIND[kind] && KIND[kind].track) || 0;
    var w = new Array(wordSpaces(tr, advOf(gap, !!(KIND[kind] && KIND[kind].it)) ) + 1).join(gap);
    return s.split(/ +/).map(function (t) { return t.split('').join(gap); }).join(w);
  }
  function clamp(v, a, b) { return v < a ? a : v > b ? b : v; }

  /* =================================================================
     PLACEMENT
     ================================================================= */
  function place(X) {
    var C = X.C, weeks = X.weeks, nWeeks = X.nWeeks, baseW = X.baseW;
    var fdata = X.field.data, FW = X.field.W, FH = X.field.H, lat = X.lat;
    var COAST = X.COAST, front = X.front, topo = X.topo || {}, EVENTS = X.events || {};
    var BANK = X.bankHolidays || {}, today = X.today;
    var zF = X.k || 1;                       // the zoom the sheet is laid on the desk at
    /* R34 (c): a reservation is made at the size the name is actually set
       at, so a bigger type table takes more ground instead of overprinting
       what is already there. One place to read the table from. */
    function KW(k, t) { return inkWidth(t, KIND[k].size / zF, !!KIND[k].it, KIND[k].track); }
    /* the marks claimed their ground first (E5), and a name takes
       what is left — survey-map.js:3735 */
    var out = [], occupied = (X.occupied || []).slice();
    /* R70: the ladder this record is plotted on. `to` carries a day's own
       commits on to the sheet's scale, `from` brings a sheet figure back
       to the record's own; both are the identity on his own sheet. */
    var LAD = (root.RoomField && root.RoomField.ladder)
      ? root.RoomField.ladder(X.scale) : { id: true, to: function (c) { return c; }, from: function (v) { return v; } };
    var nMarks = occupied.length;
    var t0 = (root.performance || Date).now();

    function alongOf(t) { return C.px + t * C.cw; }
    function acrossOf(u) { return C.py + (u + C.EDGE_ROWS) * C.ch; }
    function rowOf(wi, di) { return (C.BOUSTROPHEDON && ((baseW + wi) & 1)) ? 6 - di : di; }
    function cell(wi, di) { return [alongOf(baseW + wi + 0.5), acrossOf(rowOf(wi, di) + 0.5)]; }
    function pt(t, u) { return [alongOf(t), acrossOf(u)]; }
    /* sheet units -> the field's own lattice, and a bilinear sample:
       the same arithmetic uvOf() does on the GPU (engine.js FIELD_GLSL) */
    function fieldAt(sx, sy) {
      var fx = clamp((sx - lat.X0) / lat.DX, 0, FW - 1.001);
      var fy = clamp((sy - lat.Y0) / lat.DY, 0, FH - 1.001);
      var x0 = fx | 0, y0 = fy | 0, tx = fx - x0, ty = fy - y0, i = y0 * FW + x0;
      return fdata[i] * (1 - tx) * (1 - ty) + fdata[i + 1] * tx * (1 - ty) +
             fdata[i + FW] * (1 - tx) * ty + fdata[i + FW + 1] * tx * ty;
    }
    /* the sheet's own reservation, verbatim (survey-map.js:3116) */
    function hits(b, from) {
      for (var i = from || 0; i < occupied.length; i++) {
        var o = occupied[i];
        if (!(b.x2 < o.x1 - 6 || b.x1 > o.x2 + 6 || b.y2 < o.y1 - 2 || b.y1 > o.y2 + 2)) return true;
      }
      return false;
    }
    function reserve(b) { occupied.push(b); return b; }
    function box(cx, cy, w, h, anchor) {
      var x1 = anchor === 'end' ? cx - w : anchor === 'middle' ? cx - w / 2 : cx;
      return { x1: x1, x2: x1 + w, y1: cy - h * 0.78, y2: cy + h * 0.3 };
    }
    /* a label: a baseline POLYLINE in map units, the text centred on it.
       A straight name is a two-point line; a name along a bay or a gulf
       is the curve the water gave it. */
    function emit(kind, text, pts, opt) {
      opt = opt || {};
      out.push({ k: kind, t: text, p: pts, off: opt.off || null,
                 /* R34 (b): how far along its own baseline the name may
                    follow its feature into the window — 'curve' is the whole
                    polyline (a water name's own sound), a pair is a signed
                    map-unit range about the anchor, and nothing at all is a
                    point feature, whose name does not move. */
                 run: opt.run || null,
                 wall: opt.wall === undefined ? null : opt.wall,
                 /* R49: THE TURNED PAIR. On the two names of a horizon,
                    `turn` is the map y of the MERIDIAN's own middle — the
                    point whose squash decides which of them is inked —
                    POSITIVE on the one set across the sheet and NEGATIVE on
                    the meridian itself, so their gates are exactly one minus
                    the other. 0 on every other name on the sheet. */
                 turn: opt.turn || 0,
                 anchor: opt.anchor === undefined ? 0.5 : opt.anchor });
    }
    /* R34 (b): the construction line is 1000 units each way, not 400. It
       carries nothing but a direction and a middle — the name sits at the
       middle and the FIT picture does not move — but when the reader zooms
       in on one end of a fault or a conjecture, it is the ground the name
       has to slide along, and 400 ran out before `run` did. Two points. */
    function straight(x, y, angDeg) {
      var a = (angDeg || 0) * Math.PI / 180, L = 1000;
      return [x - Math.cos(a) * L, y - Math.sin(a) * L, x + Math.cos(a) * L, y + Math.sin(a) * L];
    }

    /* ---- the marker horizons, each named once (survey-map.js:3197) -
       R36. Every horizon is a meridian, so the name is set DOWN its own
       line the way the fault's was. The two rows it is offered are the
       plate's own top and bottom, and west of the neat line the margin is
       the record's field CONTINUED — the same rows, one field — so the
       name lands beside the line whether the line is on the plate or in
       the margin (R36 (b)), and nothing had to be said twice. */
    var MON = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN',
               'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
    function horizonName(h) {
      var p = (h.date || '').split('-');
      var d = p[2] ? String(parseInt(p[2], 10)) : '';
      return ((h.name || 'HORIZON') + ' · ' + d + ' ' + (MON[(+p[1] || 1) - 1] || '') +
              ' ' + (p[0] || '')).toUpperCase();
    }
    (X.horizons || []).forEach(function (f) {
      var t = f.t === undefined ? X.tOf(f.date) : f.t;
      if (!(t > baseW - 0.5 && t < baseW + nWeeks)) return;
      var c0 = acrossOf(-C.EDGE_ROWS), c1 = acrossOf(7 + C.EDGE_ROWS);
      var nm = spaced(horizonName(f), 'horizonLabel');
      var len = KW('horizonLabel', nm), hgt = 12 / zF;
      /* ==== R36: AND A LADDER OF ROWS, NOT TWO.
         The fault had one line and it stood five days from the west neat
         line, on ground nothing else wanted: two tries — down from the
         top row, up from the bottom — always found one. FOUR horizons
         cross the record wherever their dates fall, and Fable 5's column
         runs through the busiest ground on the plate: both ends were
         spoken for and the name was silently dropped (measured: 2 of 4
         named). This is the year figure's own answer (R11 b) — work
         inward from both edges, every row tried against the marks and
         the names, first clear row wins — and its fall-back too: if
         every row is taken the name still goes down, over whatever is
         there, because a dated line nobody can read is not a horizon. */
      var LADDER = [[0.5, 'start'], [6.5, 'end'], [1.4, 'start'], [5.6, 'end'],
                    [2.4, 'start'], [4.6, 'end'], [3.5, 'start']];
      var best = null;
      for (var li = 0; li < LADDER.length; li++) {
        var spec = LADDER[li], qq = pt(t, spec[0]), lx = qq[0] + 14, ly = qq[1];
        var lo = spec[1] === 'end' ? -len : 0, hi = spec[1] === 'end' ? 0 : len;
        var bx = { x1: lx - hgt * 0.8, x2: lx + hgt * 0.4, y1: ly + lo, y2: ly + hi };
        if (ly + lo < c0 + 2 || ly + hi > c1 - 2) continue;
        if (!best) best = { bx: bx, lx: lx, ly: ly, end: spec[1] === 'end' };
        if (!hits(bx)) { best = { bx: bx, lx: lx, ly: ly, end: spec[1] === 'end' }; break; }
      }
      if (!best) return;
      reserve(best.bx);
      /* the label reads DOWN the line: a baseline running +y */
      /* the map y of this name's OWN middle: the point whose squash decides
         which of the pair is inked, and the SAME point for both of them, so
         the two gates are exactly one minus the other (LET_VS_BODY). */
      var mid = best.ly + (best.end ? -len * 0.5 : len * 0.5);
      emit('horizonLabel', nm, straight(best.lx, best.ly, 90),
           { anchor: best.end ? 1 : 0,
             turn: 0,
             run: [c0 + 2 - best.ly, c1 - 2 - best.ly] });
    });

    /* ---- the runs of the record ----------------------------------- */
    var flat = [];
    for (var wi = 0; wi < nWeeks; wi++) for (var di = 0; di < 7; di++)
      flat.push({ d: weeks[wi][di], wi: wi, di: di });
    function runs(type) {                       // survey-map.js:1379
      var o = [], cur = null;
      flat.forEach(function (c, i) {
        var ev = EVENTS[c.d.date];
        if (ev && ev.type === type && c.d.inRange) {
          if (cur && cur.lastIdx === i - 1 && (type !== 'work-trip' || cur.names[0] === ev.name)) {
            cur.cells.push(c); cur.lastIdx = i;
            if (cur.names.indexOf(ev.name) < 0) cur.names.push(ev.name);
          } else { cur = { names: [ev.name], cells: [c], lastIdx: i }; o.push(cur); }
        } else cur = null;
      });
      return o;
    }

    /* ---- tracks: a work trip is a journey and carries its name ----- */
    /* THE TRACK NAME USED TO TAKE ITS GROUND WITHOUT ASKING (E9). It
       reserved its box and printed, with no `hits()` between — the one
       name on the plate that did — so CHESTER went down on the middle
       cell of its trip whatever was already lettered there, and at the
       fit that was Sizewell Reach: the reach's own search then ran out of
       offsets, fell through its loop and printed on top of the track.
       (8102 does the same thing at survey-map.js:3726, and got away with
       it because the SVG's tracks are drawn before the water is named
       and the two happened not to meet.) It asks now, the way a sea name
       does: the middle cell first, then the cells either side of it, each
       on the far side of the track as well as its own, and if the whole
       trip is spoken for the name goes unset rather than overprinted. */
    runs('work-trip').forEach(function (run) {
      var pts = run.cells.map(function (c) { return cell(c.wi, c.di); });
      var nm = spaced((run.names[0] || '').toUpperCase(), 'trackName', ' ');
      var tw = KW('trackName', nm), m = Math.floor(pts.length / 2);
      for (var ti = 0; ti < pts.length; ti++) {
        var p = pts[m + (ti % 2 ? -1 : 1) * Math.ceil(ti / 2)];
        if (!p) continue;
        for (var side = 0; side < 2; side++) {
          var ty = p[1] + (((p[1] > acrossOf(3.5)) !== !!side) ? -9 : 14);
          var tb = box(p[0], ty, tw, 9, 'middle');
          if (hits(tb)) continue;
          reserve(tb);
          emit('trackName', nm, straight(p[0], ty, 0),
               { run: [pts[0][0] - p[0], pts[pts.length - 1][0] - p[0]] });   /* R34 (b) */
          return;
        }
      }
    });

    /* ---- what a symbol has already taken (survey-map.js:3735) ------ */
    var markScale = 1 / zF;
    for (var a = 0; a < nWeeks; a++) for (var b = 0; b < 7; b++) {
      var dy = weeks[a][b];
      if (!dy.inRange || dy.date > today) continue;
      var ev = EVENTS[dy.date];
      if (ev || (dy.count === 0 && BANK[dy.date])) {
        var c = cell(a, b);
        reserve({ x1: c[0] - 9 * markScale, x2: c[0] + 9 * markScale,
                  y1: c[1] - 12 * markScale, y2: c[1] + 10 * markScale });
      }
    }

    /* ---- spot heights: the top four, dot and figure ---------------- */
    (function () {
      var peaks = [];
      for (var a = 1; a <= nWeeks - 2; a++) for (var b = 0; b < 7; b++) {
        /* ==== R70: SIXTY IS A HEIGHT ON THE SHEET, NOT A NUMBER OF
           SOMEBODY ELSE'S COMMITS. A spot height is set on the four
           summits, and the cut was an absolute sixty — so a record whose
           best day is thirty-eight had no summits at all, which is half
           of Tommy's complaint. The day goes UP on to the sheet's scale
           to be judged, and the figure that is inked is still the day's
           own number. On his own sheet both are the same sixty. */
        var v = weeks[a][b].count;
        if (!weeks[a][b].inRange || LAD.to(v) < 60 || weeks[a][b].date > today) continue;
        var isMax = true;
        for (var i = -1; i <= 1 && isMax; i++) for (var j = -1; j <= 1; j++) {
          if (!i && !j) continue;
          var d2 = b + j; if (d2 < 0 || d2 > 6) continue;
          if (weeks[a + i][d2].count > v) { isMax = false; break; }
        }
        if (isMax) peaks.push({ wi: a, di: b, v: v });
      }
      peaks.sort(function (x, y) { return y.v - x.v; });
      var chosen = [];
      peaks.forEach(function (p) {
        for (var i = 0; i < chosen.length; i++) if (Math.abs(chosen[i].wi - p.wi) < 3) return;
        if (chosen.length < 4) chosen.push(p);
      });
      chosen.forEach(function (p) {
        var c = cell(p.wi, p.di), s = String(p.v);
        reserve(box(c[0] + 5, c[1] + 3, s.length * 5.2 + 4, 9, 'start'));
        /* the figure stands off the dot by a constant few SCREEN pixels,
           the way the bench mark's does (styles.css .sv-spot-height) */
        emit('spotDot', '\u0001', straight(c[0], c[1], 0), { anchor: 0 });
        emit('spot', s, straight(c[0], c[1], 0), { anchor: 0, off: [5, 3] });
      });
    })();

    /* ---- the water: a bay if it has a coast, a deep if it has not -- */
    function hasShore(cells) {
      for (var i = 0; i < cells.length; i++) {
        var q = cell(cells[i].wi, cells[i].di);
        for (var a2 = -1; a2 <= 1; a2++) for (var b2 = -1; b2 <= 1; b2++) {
          if (!a2 && !b2) continue;
          if (fieldAt(q[0] + a2 * C.cw, q[1] + b2 * C.ch) > COAST) return true;
        }
      }
      return false;
    }
    var waterLabels = [];
    runs('holiday').forEach(function (run) {
      var shore = hasShore(run.cells);
      var nm = (topo.merged && topo.merged[run.names.join(' + ')]) ||
        (!shore && topo.deeps && topo.deeps[run.names[0]]) ||
        (topo.water && topo.water[run.names[0]]) || run.names[0];
      /* the name goes on the LARGEST ARM of the run, once (v13/v5 F) */
      var parts = [];
      run.cells.forEach(function (c) {
        var last = parts[parts.length - 1];
        if (last && last.wi === c.wi) last.cells.push(c); else parts.push({ wi: c.wi, cells: [c] });
      });
      parts.sort(function (x, y) { return y.cells.length - x.cells.length; });
      var pp = parts[0].cells.map(function (c) { return cell(c.wi, c.di); });
      var px2 = 0, py2 = 0;
      pp.forEach(function (q) { px2 += q[0]; py2 += q[1]; });
      px2 /= pp.length; py2 /= pp.length;
      var ang = 0;
      if (pp.length > 1) {
        var sxx = 0, syy = 0, sxy = 0;
        pp.forEach(function (q) { var dx = q[0] - px2, dy2 = q[1] - py2; sxx += dx * dx; syy += dy2 * dy2; sxy += dx * dy2; });
        ang = 0.5 * Math.atan2(2 * sxy, sxx - syy) * 180 / Math.PI;
        if (ang > 80) ang -= 180; else if (ang < -80) ang += 180;
        ang = clamp(ang, -62, 62);
      }
      waterLabels.push({ name: nm, cx: px2, cy: py2 + 12, angle: ang, cells: run.cells });
    });
    /* R2 (b): AND THE PLACEMENT AVOIDS A CLIFF. One height a word (above)
       keeps a name in one piece, but a name laid across the 2026 front
       would then stand at the top of the step with its far end in the air.
       The sheet's reservation already refuses the sea and the marks; a
       STEP IN THE GROUND along the baseline is added to what it refuses,
       so a name goes where the ground is level enough to carry it and only
       falls back to a step when every offset has one. The figure is a
       fraction of the highest ground on the plate, so it holds whatever
       the record is. */
    var STEP_MAX = 0.12 * X.maxHeight;
    function baselineStep(cx, cy, angDeg, wMap) {
      var a = (angDeg || 0) * Math.PI / 180, dx = Math.cos(a), dy = Math.sin(a);
      var half = wMap * 0.5, lo = 1e9, hi = -1e9;
      for (var i = 0; i <= 8; i++) {
        var t = -half + 2 * half * (i / 8);
        var v = Math.max(fieldAt(cx + dx * t, cy + dy * t), 0);
        if (v < lo) lo = v; if (v > hi) hi = v;
      }
      return hi - lo;
    }
    waterLabels.forEach(function (wl) {
      var w = KW('waterName', wl.name);
      var wy = wl.cy, wx = wl.cx, b = box(wx, wy, w, 12, 'middle');
      var offs = [0, 13, -13, 26, -26, 39, -39, 52, -52];
      var bestStep = 1e9, bx = 0, by = 0, bb0 = null;
      outer:
      for (var oi = 0; oi < offs.length; oi++) for (var xi = 0; xi < 3; xi++) {
        var cx = wl.cx + [0, 1, -1][xi] * w * 0.45, cy = wl.cy + offs[oi];
        var bb = box(cx, cy, w, 12, 'middle');
        if (hits(bb)) continue;
        var st = baselineStep(cx, cy, wl.angle, w);
        if (st < bestStep) { bestStep = st; bx = cx; by = cy; bb0 = bb; }
        if (st <= STEP_MAX) { wx = cx; wy = cy; b = bb; bb0 = null; break outer; }
      }
      if (bb0) { wx = bx; wy = by; b = bb0; }
      reserve(b);
      /* THE NAME BENDS TO ITS OWN WATER. On the sheet it is one straight
         baseline turned to the run's principal axis; here the same axis
         is sampled as a CURVE down the middle of the water (the
         letterEra() construction, survey-map.js:3260 — the mid-line of
         the body, smoothed three times), so a name in a crooked sound
         follows the sound. Where the water is a blob the curve comes out
         straight and the two are the same picture. */
      emit('waterName', wl.name, waterCurve(wx, wy, wl.angle, w), { run: 'curve' });
    });

    /* THE CURVE A NAME IS SET ON — letterEra()'s construction
       (survey-map.js:3283-3312): walk the name's own length, take the
       weighted middle of the WATER across the line at each step, smooth
       it three times, and set the name along that. */
    function waterCurve(cx, cy, angDeg, wMap) {
      var N = 16, a = (angDeg || 0) * Math.PI / 180;
      var dx = Math.cos(a), dy = Math.sin(a), nx = -dy, ny = dx;
      /* R34 (b): THE CURVE IS LONGER THAN THE NAME. It used to be cut to
         the name plus a fifth (0.62 of the half-width plus 20), which is
         all a name centred on it ever needs — and exactly nothing to slide
         along when the reader zooms in on one end of the sound. Another
         0.7 of the width each way is the room the name needs to follow its
         own water into the window, at no cost at the fit (the name still
         sits at the middle) and none per frame (the same 17 points). Past
         the water the mid-line sampling finds nothing and returns 0, so
         the extra simply runs straight on. */
      var mids = [], half = wMap * 1.32 + 20;
      for (var i = 0; i <= N; i++) {
        var s = -half + 2 * half * (i / N);
        var bx = cx + dx * s, by = cy + dy * s, wsum = 0, usum = 0;
        for (var u = -34; u <= 34; u += 4) {
          var v = COAST - fieldAt(bx + nx * u, by + ny * u);
          if (v <= 0) continue;
          wsum += v; usum += v * u;
        }
        mids.push(wsum > 1e-4 ? clamp(usum / wsum, -28, 28) : 0);
      }
      for (var pass = 0; pass < 3; pass++) {
        var o = mids.slice();
        for (var k = 1; k < N; k++) o[k] = (mids[k - 1] + mids[k] * 2 + mids[k + 1]) / 4;
        mids = o;
      }
      var pts = [];
      for (var j = 0; j <= N; j++) {
        var s2 = -half + 2 * half * (j / N);
        pts.push(cx + dx * s2 + nx * mids[j], cy + dy * s2 + ny * mids[j]);
      }
      return pts;
    }

    /* ---- a bank holiday's bight, and a stretch of dead calm -------- */
    for (var a3 = 0; a3 < nWeeks; a3++) for (var b3 = 0; b3 < 7; b3++) {
      var d3 = weeks[a3][b3];
      if (!d3.inRange || d3.date > today) continue;
      if (EVENTS[d3.date] || !BANK[d3.date] || d3.count >= C.BASELINE_COMMITS) continue;
      var bn = topo.bights && topo.bights[BANK[d3.date]];
      if (!bn) continue;
      var c3 = cell(a3, b3);
      emit('bight', bn, waterCurve(c3[0], c3[1] + 13, 0, KW('bight', bn)), { run: 'curve' });
    }
    var dateIndex = new Map();
    for (var a4 = 0; a4 < nWeeks; a4++) for (var b4 = 0; b4 < 7; b4++) dateIndex.set(weeks[a4][b4].date, [a4, b4]);
    Object.keys(topo.calms || {}).forEach(function (span) {
      var p = span.split('..'), q1 = dateIndex.get(p[0]), q2 = dateIndex.get(p[p.length - 1]);
      if (!q1 || !q2) return;
      var e1 = cell(q1[0], q1[1]), e2 = cell(q2[0], q2[1]);
      emit('calm', topo.calms[span], straight((e1[0] + e2[0]) / 2, (e1[1] + e2[1]) / 2 + 4, 0),
           { run: [(e1[0] - e2[0]) / 2, (e2[0] - e1[0]) / 2] });   /* R34 (b) */
    });

    /* ---- the arms of the ocean (survey-map.js:2431) ---------------- */
    (function () {
      var north = (topo.seas && topo.seas.north) || 'Mare Otiosum';
      var south = (topo.seas && topo.seas.south) || 'Mare Ignavum';
      var RUN = 5, every = 26;
      function wet(w, row) {
        var dIdx = (C.BOUSTROPHEDON && ((baseW + w) & 1)) ? 6 - row : row;
        var day = weeks[w] && weeks[w][dIdx];
        if (!day || !day.inRange || day.date > today) return false;
        var q = pt(baseW + w + 0.5, row + 0.5);
        return (COAST - fieldAt(q[0], q[1])) > 0;
      }
      [[0, 0.5, north], [6, 6.5, south]].forEach(function (arm) {
        var run = null, rr = [];
        for (var w = 0; w < nWeeks; w++) {
          if (wet(w, arm[0])) { if (!run) { run = { a: w, b: w }; rr.push(run); } else run.b = w; }
          else run = null;
        }
        rr.forEach(function (r) {
          var len = r.b - r.a + 1;
          if (len < RUN) return;
          var n = Math.max(1, Math.round(len / every));
          for (var i = 0; i < n; i++) {
            var p = pt(baseW + r.a + len * (i + 0.5) / n, arm[1]);
            var x = p[0], y = p[1] + 4;
            var w2 = arm[2].length * 19;
            var bb = box(x, y, w2, 16, 'middle');
            var lo = alongOf(baseW + r.a), hi = alongOf(baseW + r.b + 1);
            for (var t = 1; t <= 6 && hits(bb); t++) {
              var a2 = x + (t % 2 ? 1 : -1) * Math.ceil(t / 2) * C.cw * 0.8;
              if (a2 - w2 / 2 < lo || a2 + w2 / 2 > hi) continue;
              x = a2; bb = box(x, y, w2, 16, 'middle');
            }
            if (hits(bb)) return;
            reserve(bb);
            emit('seaName', arm[2], waterCurve(x, y, 0, w2), { run: 'curve' });
          }
        });
      });
    })();

    /* ---- the named oceans, and the ranges ------------------------- */
    Object.keys(topo.oceans || {}).forEach(function (span) {
      var p = span.split('..'), q1 = dateIndex.get(p[0]), q2 = dateIndex.get(p[p.length - 1]);
      if (!q1 || !q2) return;
      var w0 = Math.min(q1[0], q2[0]), w1 = Math.max(q1[0], q2[0]), mid = (w0 + w1 + 1) / 2;
      var q = pt(baseW + mid, 3.5);
      reserve(box(q[0], q[1], (w1 - w0 + 1) * C.cw * 0.9, 30, 'middle'));
      emit('oceanName', spaced(topo.oceans[span].toUpperCase(), 'oceanName', ' '),
           straight(q[0], q[1], 0),
           { run: [alongOf(baseW + w0) - q[0], alongOf(baseW + w1 + 1) - q[0]] });   /* R34 (b) */
    });
    Object.keys(topo.ranges || {}).forEach(function (span) {
      var r = topo.ranges[span], p = span.split('..');
      var q1 = dateIndex.get(p[0]), q2 = dateIndex.get(p[p.length - 1]);
      if (!q1 || !q2) return;
      var e1 = cell(q1[0], q1[1]), e2 = cell(q2[0], q2[1]);
      var cx = (e1[0] + e2[0]) / 2, cy = (e1[1] + e2[1]) / 2;
      var pk = r.kind === 'peak';
      var txt = pk ? r.name : spaced(r.name.toUpperCase(), 'rangeName');
      reserve(box(cx, cy, KW(pk ? 'peakName' : 'rangeName', txt), 14, 'middle'));
      /* R34 (b): a RANGE is a run of ground and its name may follow it; a
         PEAK is one point, and its name stays on the point. */
      emit(pk ? 'peakName' : 'rangeName', txt, straight(cx, cy, 0),
           pk ? {} : { run: [e1[0] - cx, e2[0] - cx] });
    });

    /* ---- the contour figures, set INTO a gap in their line --------
       The sheet takes the longest polyline of each INDEX level and sets
       the figure at 0.24 and 0.70 along it, turned to the local tangent
       (survey-map.js:4076). Same rule here, on a level traced out of the
       field itself. The gap is a real gap: the box goes back to the
       ground shader and the contour ink is CUT inside it, where the SVG
       could only overprint a paper-coloured halo. */
    (function () {
      var step = Math.sqrt(5) / 8, maxV = X.maxHeight + COAST;
      for (var li = 8; li * step < maxV; li += 8) {
        var lv = li * step;
        if (lv <= COAST * 1.02) continue;
        var line = traceLongest(lv);
        if (!line || line.len < 110) continue;
        /* R70: the level is a height on the sheet; the figure printed
           beside it is what that height is in THIS record's commits. */
        var cf = LAD.from(lv * lv);
        var fig = cf >= 10 || LAD.id ? String(Math.round(cf))
                                     : String(Math.round(cf * 10) / 10);
        [0.24, 0.70].forEach(function (frac) {
          var s = at(line, frac * line.len);
          if (!s) return;
          var ang = Math.atan2(s.ty, s.tx) * 180 / Math.PI;
          if (ang > 90) ang -= 180; if (ang < -90) ang += 180;
          var b = box(s.x, s.y, fig.length * 5, 9, 'middle');
          if (hits(b)) return;
          reserve(b);
          emit('contourFig', fig, straight(s.x, s.y, ang), { off: [0, 2.6] });
        });
      }
      function at(line, target) {
        var acc = 0, p = line.p;
        for (var i = 0; i + 3 < p.length; i += 2) {
          var dx = p[i + 2] - p[i], dy = p[i + 3] - p[i + 1], d = Math.hypot(dx, dy);
          if (acc + d >= target) {
            var t = d > 0 ? (target - acc) / d : 0;
            return { x: p[i] + dx * t, y: p[i + 1] + dy * t, tx: dx / (d || 1), ty: dy / (d || 1) };
          }
          acc += d;
        }
        return null;
      }
    })();

    /* A LEVEL, TRACED. Marching squares would hand back a bag of
       segments to stitch; the level is a smooth curve on a bilinear
       field, so it is walked instead — a step along the tangent, two
       Newton corrections back onto the level — which gives the polyline
       already in order, with its arc length, which is what a figure at
       "0.24 along the line" needs. Seeds are taken on a strided lattice
       and a coarse visited grid stops a line being traced twice. */
    function traceLongest(lv) {
      var STR = 4, VIS = 16, VW = Math.ceil(FW / VIS), VH = Math.ceil(FH / VIS);
      var seen = new Uint8Array(VW * VH), best = null;
      var west = (front.a0 - lat.X0) / lat.DX, east = (front.e1 - lat.X0) / lat.DX;
      /* and the plate's own rows: the field is padded a long way north and
         south of the seven days, the ground is not drawn there (mouldMask)
         and a figure must not be set on a line that is not printed */
      var north = (front.c0 - lat.Y0) / lat.DY, south = (front.c1 - lat.Y0) / lat.DY;
      function f(x, y) {
        x = clamp(x, 0, FW - 1.001); y = clamp(y, 0, FH - 1.001);
        var x0 = x | 0, y0 = y | 0, tx = x - x0, ty = y - y0, i = y0 * FW + x0;
        return fdata[i] * (1 - tx) * (1 - ty) + fdata[i + 1] * tx * (1 - ty) +
               fdata[i + FW] * (1 - tx) * ty + fdata[i + FW + 1] * tx * ty;
      }
      function g(x, y) { return [(f(x + 0.5, y) - f(x - 0.5, y)), (f(x, y + 0.5) - f(x, y - 0.5))]; }
      function inside(x, y) {
        return x > west + 1 && x < east - 1 && y > north + 0.5 && y < south - 0.5;
      }
      function walk(sx, sy, dir) {
        var x = sx, y = sy, pts = [], len = 0, px = sx, py = sy;
        for (var n = 0; n < 3000; n++) {
          var gr = g(x, y), m2 = gr[0] * gr[0] + gr[1] * gr[1];
          if (m2 < 1e-12) break;
          var tx = -gr[1] / Math.sqrt(m2), ty = gr[0] / Math.sqrt(m2);
          x += tx * 1.5 * dir; y += ty * 1.5 * dir;
          gr = g(x, y); m2 = gr[0] * gr[0] + gr[1] * gr[1];
          if (m2 < 1e-12) break;
          var e = f(x, y) - lv;
          x -= gr[0] * e / m2; y -= gr[1] * e / m2;
          gr = g(x, y); m2 = gr[0] * gr[0] + gr[1] * gr[1];
          if (m2 > 1e-12) { e = f(x, y) - lv; x -= gr[0] * e / m2; y -= gr[1] * e / m2; }
          if (!inside(x, y)) break;
          seen[((y / VIS) | 0) * VW + ((x / VIS) | 0)] = 1;
          var sxu = lat.X0 + x * lat.DX, syu = lat.Y0 + y * lat.DY;
          if (pts.length) len += Math.hypot(sxu - px, syu - py);
          pts.push(sxu, syu); px = sxu; py = syu;
          if (n > 20 && Math.abs(x - sx) < 1.2 && Math.abs(y - sy) < 1.2) break;
        }
        return { p: pts, len: len };
      }
      for (var y = Math.max(2, Math.ceil(north + 1)); y < Math.min(FH - 2, south - 1); y += STR) {
        for (var x = Math.max(2, Math.ceil(west)); x < Math.min(FW - 2, east); x += STR) {
          if (seen[((y / VIS) | 0) * VW + ((x / VIS) | 0)]) continue;
          var v0 = f(x, y), v1 = f(x + STR, y);
          if ((v0 - lv) * (v1 - lv) > 0) continue;
          var t = (lv - v0) / (v1 - v0 || 1), sx = x + t * STR;
          var A = walk(sx, y, 1), B = walk(sx, y, -1);
          var p = [];
          for (var i = B.p.length - 2; i >= 0; i -= 2) p.push(B.p[i], B.p[i + 1]);
          for (var j = 0; j < A.p.length; j += 2) p.push(A.p[j], A.p[j + 1]);
          var line = { p: p, len: A.len + B.len };
          if (!best || line.len > best.len) best = line;
        }
      }
      return best;
    }

    /* ---- TERRA INCOGNITA, and the years on their meridians --------- */
    if (!front.all && !front.none) {
      var frontW = Math.round((front.e0 - C.px) / C.cw) - baseW;
      var midT = frontW + 1 + Math.min(9, (X.RECORD.to + 1 - (baseW + frontW + 1)) / 2);
      var wp = pt(baseW + midT, 3.5);
      emit('terra', spaced('TERRA INCOGNITA', 'terra'), straight(wp[0], wp[1], 0),
           { run: [front.e0 - wp[0], alongOf(X.RECORD.to + 1) - wp[0]],   /* R34 (b) */
             /* and a WALL: grown 3x at zoom 530 the name was wider than its
                home and printed back west over the surveyed record (the
                orchestrator's merge capture, room-rev5/m34-z530.png). The
                unsurveyed paper's name starts on the unsurveyed paper. */
             wall: front.e0 });
    }
    /* THE YEAR IS ON THE GROUND, NOT IN A LAYER OVER IT. 8102 letters it
       on the screen overlay, hard against the neat line, because that is
       the only place it can measure what is already printed; the engine
       has the reservations in hand, so the figure goes on the paper at
       its own meridian of 1 January, a row down from the top edge —
       which is also the standing ruling that nothing lettered is draped
       or held in screen space. */
    (function () {
      var seenY = {};
      for (var i = 0; i < nWeeks; i++) for (var j = 0; j < 7; j++) {
        var d = weeks[i][j];
        if (!d.inRange || d.date.slice(5) !== '01-01') continue;
        var y = d.date.slice(0, 4);
        if (seenY[y]) continue;
        seenY[y] = 1;
        /* ==== R5 (d): WHICH ROW OF THE MERIDIAN.
           THE YEAR IS THE ONE FIGURE A SYMBOL CANNOT PUSH OFF THE SHEET.
           8102 letters it in the SCREEN overlay, hard against the neat
           line, where it competes with nothing standing on the ground;
           the engine sets it on the paper at its own meridian (E3), and
           once E5 put a bench mark and a milestone on that meridian the
           collision test silently dropped 2026 off the plate. E3 answered
           that by testing the figure against the LETTERING's reservations
           only and ignoring the marks — which kept the figure but printed
           it ON the bench mark's broad arrow at the fit (measured, R5).

           Both, now: FOUR rows are tried down the meridian — E3's own row
           under the north edge first, then 8102's row above the south
           one, then two between — each against the lettering AND the
           marks; the first clear row wins. If every row is spoken for the
           figure still goes down on E3's row, over whatever is there,
           because a sheet without its year is worse than a crowded one.
           Nothing else's placement is touched: the year only ever takes
           ground that was free. ==== */
        var mx = pt(baseW + i + (j / 7), 0)[0];
        if (mx < front.west - 0.5 || mx > front.e1) continue;
        /* ==== R11 (b): SEVEN ROWS, AND AN HONEST BOX.
           R5 gave the figure four rows and it still printed on bench mark
           I at the fit — not because the boxes missed each other, as that
           report guessed, but because ALL FOUR were spoken for (measured:
           0.42 by the bench mark, 1.45 by the tent, 5.55 and 6.55 by the
           fault's own label running down the sheet) and the fall-back put
           it back on row 0.42 over the mark. The ladder now works inward
           from both edges over every row of the week, so there is ground
           to find; and the box's HEIGHT is the figure's real height in map
           units (12 CSS px / the fit's zoom), not 12 map units — at the
           fit that is 20, and the figure was reserving half of itself. ==== */
        var wY = KW('yearFig', y);
        var ROWS = [0.42, 6.55, 1.45, 5.55, 2.45, 4.55, 3.5], q = null, bb = null;
        for (var ri = 0; ri < ROWS.length; ri++) {
          var qq = pt(baseW + i + (j / 7), ROWS[ri]);
          var b2 = box(qq[0] + 9, qq[1], wY, 12 / zF, 'start');
          if (!q) { q = qq; bb = b2; }            /* E3's row, the fall-back */
          if (!hits(b2)) { q = qq; bb = b2; break; }
        }
        reserve(bb);
        emit('yearFig', y, straight(q[0] + 9, q[1], 0), { anchor: 0 });
      }
    })();


    return { labels: out, ms: +((root.performance || Date).now() - t0).toFixed(1) };
  }

  /* =================================================================
     THE ATLAS, THE LAYOUT AND THE DRAW  (main thread)
     ================================================================= */
  /* the bands the atlas is rasterised at. One band is picked from the
     device ratio and the camera's own magnification, so a glyph is drawn
     within a factor of 2 of the size it was cut at and never softer. */
  var LETZ = (function () {
    var m = typeof location !== 'undefined' && /[?&]letz=([-\d.]+)/.exec(location.search);
    return m ? parseFloat(m[1]) : 0.0012;
  })();
  /* R11 (a): `?far=0` is the pair — every name at every distance, the way
     it was — and `?far=<k>` scales every kind's threshold together. */
  var FAR = (function () {
    var m = typeof location !== 'undefined' && /[?&]far=([-\d.]+)/.exec(location.search);
    return m ? parseFloat(m[1]) : 1;
  })();
  /* R57 + R66: the word's run is fitted to the country it crosses, and the
     letterform lies on that fall's own plane. R57 left the fit OFF because
     half of it was missing — the cell stayed a HORIZONTAL patch (R34 a), so
     the letters marched down a line that was not their own baseline and
     Devon Sound came out a smear (room-rev7/R57-devon-after.png). R66 puts
     the cell on the fall (?letcell=), so the pair SHIPS: `?letfall=0` is
     R2 (b)'s single level, kept until Tommy has seen the two side by side. */
  var LETFALL = (function () {
    return typeof location === 'undefined' || !/[?&]letfall=0/.test(location.search);
  })();
  /* R66: which plane the letterform is printed on.
     0 — the HORIZONTAL patch at the glyph's height, R34 (a) as built, which
         is what ?letfall=1 alone drew (the smear).
     1 — the TANGENT carries the run's own slope: an orthogonal frame, no
         extra tap, the letters step along the line they are set on. On its
         own it is worse than 0 — the run collapses and nothing holds the
         body up (room-rev7/r66-specs/d-cell1.png, a hairline).
     2 — and the body carries the GROUND's own rise across the run, so the
         cell lies in the surface's tangent plane: the normal is the
         ground's. Two ground taps a vertex, unmeasurable. THE DEFAULT.
     The plan footprint is the same in all three: a letter on a slope is
     foreshortened exactly as the slope is and no more, which is what paper
     does when the relief rises under it and takes its print along. */
  var LETCELL = (function () {
    var m = typeof location !== 'undefined' && /[?&]letcell=([0-9])/.exec(location.search);
    return m ? parseFloat(m[1]) : 2;
  })();
  /* R66: the cap on the cross-slope a letterform's cell will take, in map
     units of rise per map unit across (1.0 is 45 degrees). `?letroll=<k>`. */
  var LETROLL = (function () {
    var m = typeof location !== 'undefined' && /[?&]letroll=([-\d.]+)/.exec(location.search);
    return m ? parseFloat(m[1]) : 1;
  })();
  /* R57: the CUT is the variant, not the default — it takes the glyphs whose
     own point is on the cut's band clean out of the word, and at his view
     that is "Carcass . . . e" (room-rev7/R57-var-both-b72.png against
     R57-var-fall-b72.png, 614 px of the difference). Tommy picks: it is
     ?letcut=1, and neither half ships as the default. */
  var LETCUT = (function () {
    return typeof location !== 'undefined' && /[?&]letcut=1/.test(location.search);
  })();
  /* R11 (c): the walk toward the eye — its step and the cap on the raise,
     both in map units (a column is 40). `?skyline=0` is the pair. */
  var SKYLINE = (function () {
    var m = typeof location !== 'undefined' && /[?&]skyline=([-\d.,]+)/.exec(location.search);
    if (!m) return [48, 70];
    var v = m[1].split(',').map(parseFloat);
    return [v[0] || 0, v.length > 1 ? v[1] : 62];
  })();
  /* R34 (a): the cap on the growth. A loupe over a map does not make the
     names a foot tall: the type grows with the sheet up to this multiple of
     its fit size and then holds. 3 is the item's figure; `?letgrow=` is the
     dial and `?letgrow=1` is the old picture with the new type table. */
  var GROW_CAP = (function () {
    var m = typeof location !== 'undefined' && /[?&]letgrow=([-\d.]+)/.exec(location.search);
    return m ? Math.max(1, parseFloat(m[1])) : 3;
  })();
  /* R34 (a): `?letmap=0` — the quad back in device pixels about the
     anchor's projection, the rule before R34. `?letup=cam` is built on the
     device-pixel path (it holds the baseline to the camera), so it implies
     it. */
  var LETMAP = (function () {
    if (typeof location === 'undefined') return true;
    if (/[?&]letup=cam/.test(location.search)) return false;
    return !/[?&]letmap=0/.test(location.search);
  })();
  /* R34: `?letdbg=1` reports which names were slid into the window and what
     the window was, for the harness — off, it costs one comparison a frame. */
  var DBG = typeof location !== 'undefined' && /[?&]letdbg=1/.test(location.search);
  var LETTHIN = !(typeof location !== 'undefined' && /[?&]letthin=0/.test(location.search));
  var LETVIS = typeof location !== 'undefined' && /[?&]letvis=1/.test(location.search);
  /* R34 (b): ?letrun=0 — the names stay at the middle of their features, the
     rule before R34, for measuring what the follow costs and buys. */
  var LETRUN = !(typeof location !== 'undefined' && /[?&]letrun=0/.test(location.search));
  /* ==== R37 (a): `?letcoll=0` — the screen test off, the sheet's own
     reservations alone, which is the rule before R37. `?letcoll=<ms>`
     sets the fade's own time (120 ms is the item's figure). ==== */
  var LETCOLL = (function () {
    if (typeof location === 'undefined') return 120;
    var m = /[?&]letcoll=([-\d.]+)/.exec(location.search);
    return m ? parseFloat(m[1]) : 120;
  })();
  var LETFORE = (function () {
    var m = typeof location !== 'undefined' && /[?&]letfore=([-\d.]+)/.exec(location.search);
    return m ? parseFloat(m[1]) : 0.55;
  })();
  var BANDS = [16, 24, 32, 48, 64, 96];
  /* R34 (a): the ladder the PLATE climbs. The atlas is square in the band,
     so 96 is nine times the pixels of 32 — about 10 MB of texture and some
     23 ms to cut and upload, which is a hitch in the middle of a zoom. 64
     is 4.5 MB and about 10 ms, and at the cap it draws waterName's 13.5 x
     1.75 x 3 = 71 device px from a 64 px cut: 1.11x, which is inside the
     "within a factor of two" the ladder was built on. Only terra and the
     ocean (20 and 19 css px, 105 and 100 device px at the cap) are magnified
     past 1.5x, and they are the two words nobody reads letter by letter. */
  var BAND_MAX = 64;
  var CHARS = (function () {
    var s = '';
    for (var i = 32; i < 127; i++) s += String.fromCharCode(i);
    return s + '  ‘’“”–—·°éíáúóèàöäüñçÉÍ£†';
  })();

  /* ONLY THE GLYPHS THE PLATE ACTUALLY SETS. The sheet has one face in
     two styles and about sixty names; cutting all of ASCII in both
     styles is four times the texture for glyphs nothing on the plate
     ever asks for (the furniture SVG subsets its own fonts to 65 glyphs
     for the same reason, E1 §(f)). */
  /* R8: THE MARGIN'S OWN GLYPHS. The month rulers are drawn live now
     (ruler.js) out of THIS atlas, so the twelve month names, the year
     figures and the day figures have to be cut into it — they are not
     in any label on the plate. One string, reserved before the first
     cut; the alternative was a second atlas for thirty-eight glyphs. */
  var EXTRA_R = '';
  LET.reserve = function (s) { EXTRA_R += s; };

  function charsetOf(labels) {
    var r = {}, i = {};
    for (var e = 0; e < EXTRA_R.length; e++) r[EXTRA_R[e]] = 1;
    (labels || []).forEach(function (L) {
      var K = KIND[L.k]; if (!K) return;
      var t = K.it ? i : r;
      for (var j = 0; j < L.t.length; j++) if (L.t[j] !== ' ') t[L.t[j]] = 1;
    });
    function srt(o) { return Object.keys(o).sort().join(''); }
    return { r: srt(r), i: srt(i) };
  }

  /* R18: EVERY CUT HAS AN IDENTITY. A band is not one — two different
     atlases can be rasterised at the same band (the ruler's own 32 and
     the plate's own 32 at devicePixelRatio 2) and their packings do not
     agree, so anything that CACHES CUTS out of an atlas has to key on
     the cut itself, not on the size it was cut at. `gen` is that key:
     one number per canvas ever rasterised, monotone for the page. */
  var ATLAS_GEN = 0;
  function buildAtlas(band, need) {
    var t0 = performance.now();
    var pad = Math.max(3, Math.round(band * 0.30));
    var cw = Math.ceil(band * 1.15) + pad * 2, chh = Math.ceil(band * 1.30) + pad * 2;
    var originX = pad + Math.round(band * 0.10), originY = pad + Math.round(band * 0.86);
    /* an EMPTY string is an answer ('cut no italics'), not a missing one:
       `need.i ? … : CHARS` cut the whole of ASCII in italic for a caller
       that asked for none (R18 — the ruler's own atlas was 149 glyphs
       where it wanted 29). */
    var SET = [need && need.r !== undefined ? need.r : CHARS,
               need && need.i !== undefined ? need.i : CHARS];
    var n = SET[0].length + SET[1].length, perRow = Math.ceil(Math.sqrt(n * chh / cw));
    var rows = Math.ceil(n / perRow);
    /* NPOT is fine: CLAMP_TO_EDGE, LINEAR, no mipmaps (WebGL 2) */
    var W = perRow * cw, H = rows * chh;
    var cv = document.createElement('canvas'); cv.width = W; cv.height = H;
    var ctx = cv.getContext('2d', { alpha: true });
    ctx.clearRect(0, 0, W, H);
    ctx.fillStyle = '#fff'; ctx.textBaseline = 'alphabetic'; ctx.textAlign = 'left';
    var g = {}, k = 0, advR = {}, advI = {};
    for (var s = 0; s < 2; s++) {
      var it = s === 1, CS = SET[s];
      ctx.font = (it ? 'italic ' : '') + band + 'px "Sorts Mill Goudy LN", "Sorts Mill Goudy", Georgia, serif';
      for (var i = 0; i < CS.length; i++, k++) {
        var ch = CS[i];
        var col = k % perRow, row = (k / perRow) | 0;
        var x = col * cw, y = row * chh;
        if (ch === '\u0001') {
          /* the spot height's own dot: r = 1.5 CSS px at the sheet's
             setting, cut into the atlas so a spot height is one draw */
          ctx.beginPath();
          ctx.arc(x + originX, y + originY - band * 0.12, band * 0.1875, 0, 6.2832);
          ctx.fill();
        } else ctx.fillText(ch, x + originX, y + originY);
        g[(it ? 'i' : 'r') + ch] = [x / W, y / H, (x + cw) / W, (y + chh) / H];
        (it ? advI : advR)[ch] = ch === '\u0001' ? 0 : ctx.measureText(ch).width / band;
      }
    }
    return { cv: cv, W: W, H: H, cw: cw, ch: chh, originX: originX, originY: originY,
             band: band, gen: ++ATLAS_GEN, rects: g, adv: { r: advR, i: advI }, n: n,
             ms: +(performance.now() - t0).toFixed(2), bytes: W * H * 4 };
  }
  function pow2(v) { var p = 1; while (p < v) p *= 2; return p; }


  /* ---------------- the program ---------------------------------- */
  /* ==== R52: AND THE NAME TAKES THE LANTERN TOO ======================
     R48 warmed the ground fragment on the lantern's pool at night and left
     this program — the names, which are drawn OVER it — cold: measured
     over the TERRA INCOGNITA band at 23:00, the paper gained 15.6 of r-b
     and the glyphs' ink gained 0.8 (REPORT-room-R48). A name is printed on
     the paper, so it is lit by the paper's lamp. The same five lines, the
     same two uniforms off RoomObjects.lampSheet(S) — ONE lamp on the desk
     — applied LAST, and uLampN is exactly 0 by day so the daylight picture
     is the one it was to the bit.

     THE HALO TAKES IT TOO, and has to: the halo is PAPER (the sheet's
     2.6px paper stroke under every name), so a cold halo at night would
     have printed a ring of daylight paper on warm paper — a plainer fault
     than the cold ink was. One lampTemp after the mix warms both. ==== */
  var LAMP_FS = `
uniform vec4 uLampS;    // R52: the lantern in sheet units: x, y, and the pool's two radii
uniform float uLampN;   // and the night, which is nought by day
vec3 lampTemp(vec3 c, vec2 wp){
  if (uLampN <= 0.0) return c;
  vec2 d = (wp - uLampS.xy)/uLampS.zw;
  float pool = 1.0 - clamp((length(d) - 0.20)/0.80, 0.0, 1.0);
  vec3 t = mix(vec3(0.985,0.999,1.022), vec3(1.018,0.998,0.930), pool);
  /* LK1: and the lamp is the light. The paper outside the pool falls into
     shadow at night (to half), and into a dusk dimness before it. */
  float rr = length(d); float lit = 0.40 + 0.58*(1.0 - smoothstep(0.12, 0.95, rr));
  return c*mix(vec3(1.0), t*lit, uLampN);
}
`;
/* ==== FD2 (a), 2026-09-20: THE PAPER THIS INK IS LYING ON ============
   objects.js's own chunk: paperSurf (the sheet's face, which XFORM's
   sheetToClip puts every map point on) and paperInk (the light the sheet
   throws on ink printed on it). The stub is for a harness that loads this
   module without the desk. ==== */
var PAPER_FS = (root.RoomObjects && root.RoomObjects.paperInkGLSL)
  ? root.RoomObjects.paperInkGLSL()
  : 'float paperSurf(vec2 p){ return 0.0; }\nvec3 paperInk(vec3 c, vec2 p){ return c; }\nconst float uPapInk = 1.0;\n';

  var LET_VS_HEAD = `#version 300 es
precision highp float; precision highp sampler2D;
layout(location = 0) in vec2 a;
layout(location = 1) in vec4 iA;    // anchor x, y (map) and a one-unit tangent
layout(location = 2) in vec4 iQ;    // the quad about the pen, in DEVICE px
layout(location = 3) in vec4 iUV;   // the glyph's rect in the atlas
layout(location = 4) in vec4 iC;    // ink rgb, and the whole glyph's alpha
layout(location = 5) in vec2 iH;    // the halo's radius, in atlas uv
layout(location = 6) in vec4 iW;    // THE WORD'S OWN BASELINE: its start (map) and its vector
layout(location = 7) in float iF;   // R11 (a): the foreshortening the kind survives down to
layout(location = 8) in float iS;   // R37 (a): the screen test's own fade for this name
layout(location = 9) in float iT;   // R49: on a TURNED twin, the map y of its partner's middle
out vec2 vUv; out vec4 vC; out vec4 vRect; out vec2 vHalo;
/* R52: the flat sheet point the lantern's pool is measured in — the same
   space engine.js's ground fragment carries, mapToSheet(m,h).xy. */
out vec2 vSheet;
uniform vec2 uVpx;
uniform float uRelief;
uniform float uLetUp;
uniform float uLetMap;  // R34 (a): the quad is four corners of the PAPER
uniform float uLetThin; // ?letthin=0: R11's crowding fades off, for measuring
uniform float uLetFore; // R34 (a): the floor under the LETTERFORM's own squash
uniform float uLetFall;  // R57: the run is fitted to the country, not levelled
uniform float uLetCell;  // R66: which plane the letterform is printed on (0/1/2)
uniform float uLetRoll;  // R66: the cap on the cross-slope the cell will take
uniform float uLetCut;  // R57: and no name is inked on the scarp's cut face
uniform float uLetZ;
uniform vec4 uPortal;   // R9 (c): the window, in device px — l, t, w, h
uniform float uFlatPx;  // R11 (a): device px per map unit with the sheet flat
uniform vec2 uSkyline;  // R11 (c): the step and the cap of the raise, in map units
`;
  var LET_VS_BODY = `
/* THE GROUND UNDER A POINT, the same arithmetic the plate's own mesh
   does — a letter has to stand on exactly the surface that is drawn or
   the depth test (below) cuts it. */
float groundAt(vec2 m){
  /* R40: and east of the cut the ground is the DOWNS, which is the one
     place a name is lettered on country the survey has not walked —
     TERRA INCOGNITA itself. Read without them the name lay at the datum
     with a hillside standing in front of it and the depth test ate the
     first word (#2194,330,1273,38,-6). terraOf is 0 west of the cut, so
     every other name on the sheet stands exactly where it stood. */
  float bed = riseOf(fieldAt(m) + terraOf(m));   /* R4: the ground rises at load */
  return max(bed, 0.0) * mouldMaskT(m) * EXAGC * uRelief;
}
vec2 scrOf(vec4 c){ return vec2((c.x/c.w*0.5+0.5)*uVpx.x, (0.5 - c.y/c.w*0.5)*uVpx.y); }
void main(){
  vSheet = vec2(0.0);       /* R52: set for the two cut returns below */
  /* THE NAME IS ON THE GLASS, AND UPRIGHT TO THE SHEET. The anchor is a
     point on the GROUND — it rides the relief when the sheet is raised —
     but the quad is laid out in DEVICE pixels about that point's
     projection, along the projection of the baseline the name has on the
     paper. So a name is never draped: it does not foreshorten, it does
     not soften, and it is the same size on screen at every zoom and
     every tilt, which is what calc(Npx / var(--z)) buys on the sheet. */

  /* R2 (b): A WORD IS ONE OBJECT, SO IT HAS ONE HEIGHT. Every glyph used
     to take the ground under its own origin, and a name that crossed the
     2026 front came out in two pieces — Tommy's "Carcasonne writes as C +
     arcasonne with the C at the top of a hill and the rest at the bottom".
     The word's OWN baseline (iW: its start and its vector, the run the
     glyphs actually occupy, not the 400-unit construction line) is sampled
     at nine points and the HIGHEST is taken for every glyph of the word:
     one baseline, and no glyph buried in its own hill now that the letters
     are depth-tested. Nine taps on four vertices a glyph is ~0.01 ms of
     the frame at 380 glyphs (measured, REPORT-room-R2). */
  /* ==== R57: ONE HEIGHT CAN BE ONE STRAIGHT RUN — the VARIANT ?letfall=1.
     Tommy: "i can see carcassone through the scarp though"
     (#1247,424,1469,53,72). MEASURED first: ?skyline=0 and ?letz=0 both
     draw the fault unchanged, and the ground's own earth term painted
     magenta shows the scarp is FRAGMENT COLOUR on the one surface, not a
     wall in the depth buffer — so no depth rule can hide a name from it
     (room-rev7/r57-specs/crop-{sky0,letz0,earth,pos}-b72.png). What lifts
     a name on to the cut is R2 (b)'s LEVEL: the highest sample under the
     run carries a word that crosses a fall a long way above the country
     it names, and the lifted word lands on the pixels of the cut behind
     it. The depth test is right; the word is in the air.
     So the run is FITTED instead of levelled: the lowest straight line on
     or above R2's nine samples (the end-to-end fall gives the slope, the
     samples the height). The word is still ONE straight run of type, no
     glyph is buried in its own hill, and on flat ground the slope is
     nought and the line is exactly R2's level — the fit cannot move by a
     pixel (measured, 0 px at 1.75 and at 1.25).
     IT DOES NOT SHIP. At R2's own view (#1408,431,1060,53,0) it costs
     DEVON SOUND: a name set down a steep fall is compressed by the tilt
     until it is a smear (room-rev7/R57-devon-after.png against -before).
     Tommy picks; REPORT-room-R57.md has the contact sheet. ==== */
  /* ==== LP5: EVERY HEAVY FUNCTION IS EVALUATED AT ONE CALL SITE.
     Direct3D's compiler inlines a function at every call site and its own
     time grows faster than the inlined text, so what this program cost at
     boot was never the arithmetic — it was the COUNT. Measured on Tommy's
     card (RTX 3070 Ti, Chrome 153, ANGLE Direct3D 11) the lettering's
     vertex shader compiled alone in 3.4 s, second only to the ground's,
     with groundAt written out eleven times and the sheet's drape chain
     (scrOf over sheetToClip over mapToSheet, and bookM / bkRamp /
     bookLift / creaseGrad under that) ten and eleven times over.
     So the SAMPLE POINTS are gathered instead of the code. Three loops —
     one for the ground, two for the drape — each holding exactly one call
     to the chain, and every point carries the argument expression that
     was written at the site it came from: same functions, same order of
     floating-point operations, so every vertex lands on the same clip
     position to the bit. Nothing about the picture, the gates or the
     early returns moves; a group whose gate is shut takes no sample at
     all, exactly as the branch it came from made no call. The two drape
     loops are two because the turn (R2 c) and the portal's cut (R9 c)
     stand between them and both read a projected point; within a loop the
     iterations are SEQUENTIAL, so a later point may be laid out from an
     earlier one's result and no third loop is needed for the quad.
     lieH and tVnoise are still written out five times each — both live in
     the shared FIELD and XFORM chunks, which this file does not own. ==== */
  float gA = 0.0, gB = 0.0;
  float hB = 0.0;                                 /* the run's own fall */
  float hA = -1.0e9;
  /* the glyph's own place along the run, so every glyph of the word sits
     on the SAME line and the word is still one object. */
  float wlen2 = max(dot(iW.zw, iW.zw), 1e-6);
  /* R66: the fitted line's own slope — height per map unit ALONG the run,
     and the run's direction to read it in. Both are the WORD's, so every
     glyph of a name is set on one plane (R2 b: a word is one object).
     LP5: the run's length and direction are read BEFORE the ground's loop
     rather than after it — they are the word's own attributes and nothing
     in them waits on a sample, so the value is the value it always was.
     The slope waits on hB and is taken below. */
  float wlen = sqrt(wlen2);
  vec2 runDir = iW.zw / wlen;
  /* ==== R71 (d): THE HEIGHT IS THE WHOLE WORD'S, BANKS INCLUDED. Tommy,
     at #1101,431,2120,52,78 on a wide screen: "how do you explain this,
     then?" — Carcassonne read down the face again, from the east. Its lake
     is the top of a reversed week, so the name runs up the water to the
     record's rim, where the lake's floor lies at sea level and opens
     through the north face as a notch; the nine taps along the BASELINE
     are all water, so the word was set at the water's own level and read
     through the notch. The letters' bodies reach across the run on to the
     banks, so the footprint is sampled too — a half body either side of
     each tap — and the word stands at the highest ground under ANY of its
     ink, which for a name on water at the plateau's edge is the bank, and
     the bank then hides it from the east as Tommy asked ("sit wholly on
     the plateau, and it's hidden from the east"). 27 taps for 9; nothing
     flat, since groundAt is nought there. iQ.w is the cell's height in
     map units on the paper path and device pixels on the other, so the
     reach is taken on the paper path only. ==== */
  vec2 wn = (uLetMap > 0.5 && dot(iW.zw, iW.zw) > 1e-6)
          ? normalize(vec2(-iW.w, iW.z)) * abs(iQ.w) * 0.5 : vec2(0.0);
  /* R11 (c)'s walk toward the eye, R49's twin and R66's lateral pair, all
     set up here so the one loop below can ask for their points. Each keeps
     its own gate: the sample is not taken when the branch would not have
     called for it. cot is read under the gate because uAtt.y is only known
     to be over a quarter there. */
  bool skyOn = uSkyline.x > 0.0 && uAtt.y > 0.25;
  vec2 toEye = vec2(uYaw.y, uYaw.x);
  float cot = 0.0;
  if (skyOn) cot = uAtt.x / uAtt.y;
  bool twinOn = abs(iT) > 1.5;
  vec2 mP = vec2(iW.x, abs(iT));  /* the WORD's x, not the glyph's: R2 (b) */
  bool rollOn = uLetMap > 0.5 && uLetCell > 1.5 && uLetFall > 0.5 && uRelief > 0.001;
  float eC = max(uLat0.x, 0.25);
  vec2 nmR = vec2(-runDir.y, runDir.x);
  vec2 mid = iW.xy + iW.zw * 0.5;
  /* ==== THE GROUND'S ONE LOOP. Fifty points, in the order the code below
     used to ask for them:
       0        the head of the word's own baseline           (R57's gA)
       1        its tail                                      (R57's gB)
       2 .. 28  nine taps along the run, each with the two half-body
                reaches either side of it                     (R2 b, R71 d)
       29 .. 46 six steps toward the eye, three rays to a step (R11 c)
       47       the meridian twin's own middle                (R49)
       48, 49   the lateral pair across the run               (R66)
     gi is the step within a group and gw which of its three rays; both are
     carried rather than divided out, since an integer division on every
     iteration is dearer than the two adds it replaces. ==== */
  float need = 0.0, hP = 0.0, gLat = 0.0, gLbt = 0.0;
  float sRun0 = 0.0, sRun1 = 0.0;   /* the first two rays of a tap */
  int gi = 0, gw = 0;
  for (int k = 0; k < 50 + uZero; k++) {
    if (k == 2) { gi = 0; gw = 0; }
    else if (k == 29) { gi = 1; gw = 0; need = hA + hB * 0.5; }
    bool on = true;
    float gu = 0.0, gt = 0.0;
    vec2 gp = iW.xy;
    if (k == 1) gp = iW.xy + iW.zw;
    else if (k >= 2 && k < 29) {
      gu = float(gi) * 0.125;
      vec2 pw = iW.xy + iW.zw * gu;
      gp = gw == 0 ? pw : (gw == 1 ? pw + wn : pw - wn);
    }
    else if (k >= 29 && k < 47) {
      /* THREE RAYS, not one: a ridge that hides the head of a word does
         not hide its tail, and one ray off the middle raised REYKJAVIK
         FJORD just far enough to show its last three letters (measured,
         room-rev2/R11-skyonly-t70-y0.png). The word is one object with one
         height (R2 b), so the height has to clear the worst of the three. */
      on = skyOn;
      gt = float(gi) * uSkyline.x;
      vec2 e = toEye * gt;
      gp = gw == 0 ? iW.xy + e : (gw == 1 ? iW.xy + iW.zw * 0.5 + e : iW.xy + iW.zw + e);
    }
    else if (k == 47) { on = twinOn; gp = mP; }
    else if (k >= 48) { on = rollOn; gp = k == 48 ? mid + nmR * eC : mid - nmR * eC; }
    if (on) {
      float g = groundAt(gp);
      if (k == 0) gA = g;
      else if (k == 1) { gB = g; hB = uLetFall > 0.5 ? gB - gA : 0.0; }
      else if (k < 29) {
        if (gw == 0) sRun0 = g;
        else if (gw == 1) sRun1 = g;
        else hA = max(hA, max(sRun0, max(sRun1, g)) - hB * gu);
      }
      else if (k < 47) need = max(need, g - gt * cot);
      else if (k == 47) hP = g;
      else if (k == 48) gLat = g;
      else gLbt = g;
    }
    gw++;
    if (gw > 2) { gw = 0; gi++; }
  }
  float runSlope = hB / wlen;
  float h = hA + hB * 0.5;      /* the word's own middle, for R11 (c) */

  /* ==== R11 (c): A NAME RIDES ITS RIDGE'S SKYLINE.
     R2 put the letters in the depth buffer, which is honest — a hill hides
     what is behind it — but it hides the WHOLE word the instant its anchor
     goes behind a ridge, even where the word would have stood clear of the
     skyline (REPORT-room-R2 OPEN 3). A name is not a thing standing on the
     ground like a beacon: it is written ON the ground, and a surveyor sets
     it where it can be read. So the ground between the eye and the word is
     walked, and the word is raised to the lowest height that puts its
     baseline ON the highest silhouette in the way.

     The arithmetic, orthographically (mapToSheet): a point at r.y and
     height z sits at v = r.y*cos(tilt) - z*sin(tilt), and SMALLER v is
     higher on screen. The eye is at +r.y — rot(yaw) sends map direction
     (sin, cos) to r = (0, 1) — so ground t map units toward it, at height
     g, hides a word at height z_w when z_w <= g - t*cos/sin. The largest
     such figure over the walk is the height that grazes the skyline.
     Nine taps, the same order as the baseline's. The raise is CAPPED
     (uSkyline.y): a name behind a mountain stays behind it rather than
     float in the sky, which is R2's ruling for the cases it still owns.
     Off below a quarter of a radian of tilt, so the flat sheet cannot
     move; ?skyline=0 is the pair, ?skyline=<step>,<cap> the dial.
     LP5: the walk itself is the ground loop's second group above; what is
     left here is the cap, which is the only part that is not a sample. */
  if (skyOn) h = min(need, h + uSkyline.y);
  /* R57: R11 (c) raises the WORD, so what it leaves is a raise the whole
     run carries — the line is not bent by it. */
  float hR = h - (hA + hB * 0.5);
  /* R66: the cross-slope is the WORD's and is read once — R2 (b), a word
     is one object. Read per GLYPH it rolls every letter by its own angle
     and a name running out on to a bank has its last letter stretched
     into a spike (measured: Devon Sound's final d,
     room-rev7/r66-specs/d-cell2-perglyph.png). Read once, at the word's
     middle and across its own run, the name lies on one plane.
     CAPPED at uLetRoll (1.0 — 45 degrees, a body stretched by 1.41):
     past that the letterform stops being type, which is R34 (a)'s own
     argument for the floor under the body. At his views it never bites
     (?letroll=3 is 0 px against the default). */
  float nzW = clamp((gLat - gLbt) / (2.0 * eC), -uLetRoll, uLetRoll);

  /* R2 (c): AND IT READS LEFT TO RIGHT. A baseline set down the sheet (the
     fault's label) projects BACKWARDS once the sheet is turned past a
     right angle, and the name came out rotated 180 degrees. The whole word
     is turned back about its own middle — in MAP units, so the glyph keeps
     an honest depth — which reverses the order of the pens and rights the
     glyphs in one move. ONLY a name whose baseline points into the left
     half-plane is turned: a name set down the sheet (Devon Sound in its
     sound) is a name set down the sheet at every attitude, and turning
     those as well moved the FLAT picture, which is not allowed to move. */
  vec2 mA = iA.xy, tA = iA.zw;
  /* ==== THE DRAPE'S FIRST LOOP. Five points, none of which waits on the
     turn below: R57's two ends of the run, and R49's three about the
     meridian twin's own middle (which are read off the WORD, not the
     glyph, so they do not wait on mA either). R49's are skipped whole
     where there is no twin. ==== */
  /* R57: the two ends of the run are the two ends of the LINE now. */
  vec2 ws = vec2(0.0), we = vec2(0.0), sP = vec2(0.0), sPy = vec2(0.0), sPa = vec2(0.0);
  for (int k = 0; k < 5 + uZero; k++) {
    vec2 pm = iW.xy; float ph = hA + hR;
    if (k == 1) { pm = iW.xy + iW.zw; ph = hA + hB + hR; }
    else if (k >= 2) {
      if (!twinOn) break;
      pm = k == 2 ? mP : (k == 3 ? mP + vec2(0.0, 1.0) : mP + vec2(uYaw.x, -uYaw.y));
      ph = hP;
    }
    vec2 s = scrOf(sheetToClip(mapToSheet(pm, ph)));
    if (k == 0) ws = s;
    else if (k == 1) we = s;
    else if (k == 2) sP = s;
    else if (k == 3) sPy = s;
    else sPa = s;
  }
  /* ==== R9 (c): A FIGURE WHOSE BOX LEAVES THE WINDOW IS NOT SET.
     The contour figures were cut in half by the neat line at zoom
     (room-rev2/s-zoom400-flat.png: a "2..." half off the south edge).
     The cut is the WINDOW's, not the plate's — at zoom the plate runs
     far past the neat line on every side — so the test has to be
     against the portal in device pixels, and it has to be right under
     a PAN, which the layout deliberately is not (it runs once per zoom
     so a drag lays out no glyph). So it is done HERE, on the word's own
     baseline, which every glyph already carries for R2 (b): four extra
     compares on a vertex that has already projected both ends.
     WHICH kinds are cut this way rides in the SIGN of the halo radius
     — the kinds that ask for it (contourFig) have no halo at all, so
     nothing is lost by spending the sign, and no attribute is added. */
  if (iH.x < 0.0) {
    /* R34 (a): iQ is in MAP units on the paper path, so the figure's own
       half-height has to be carried to device pixels before it is compared
       with the portal — uFlatPx is exactly that rate. */
    float ex = abs(iQ.w) * (uLetMap > 0.5 ? uFlatPx : 1.0) + 1.0;
    vec2 lo = min(ws, we) - ex, hi = max(ws, we) + ex;
    if (lo.x < uPortal.x || lo.y < uPortal.y ||
        hi.x > uPortal.x + uPortal.z || hi.y > uPortal.y + uPortal.w) {
      gl_Position = vec4(2.0, 2.0, 2.0, 1.0);
      vUv = vec2(0.0); vC = vec4(0.0); vRect = vec4(0.0); vHalo = vec2(0.0);
      return;
    }
  }
  vec2 wd = we - ws;
  float wl = length(wd);
  if (wl > 1e-4) {
    float cx = wd.x / wl;
    if (cx < -0.09) {
      mA = 2.0 * iW.xy + iW.zw - mA;
      tA = -tA;
    }
  }
  /* R57: the glyph's own place along the word's baseline, after R2 (c)'s
     turn, picks its height off the line — so the word is one run of type
     lying on the country. uLetFall off (the default) makes hB 0 and this
     R2 (b)'s level, to the bit. */
  h = hA + hB * clamp(dot(mA - iW.xy, iW.zw) / wlen2, 0.0, 1.0) + hR;
  /* ==== R57: A NAME IS NOT INKED ON CUT EARTH — the VARIANT ?letcut=1.
     The earth is asked the same question the ground asks itself (the
     cut's x band, the record's own gradient, its own rows) at the glyph's
     own point, and where the answer is earth the letter is not set. Two
     field fetches inside a 19-unit band; every other glyph pays one
     compare on s.x. It does not ship either: it takes whole glyphs out of
     a word — "Carcass . . . e" at his view — and TERRA INCOGNITA, whose
     glyphs stand east of the band, is untouched by it. ==== */
  if (uLetCut > 0.5 && uScarp.y > 0.0 && uRelief > 0.001 &&
      mA.x > uScarp.x - 2.0 && mA.x < uScarp.x + uScarp.y + 3.0) {
    float gxE = (texture(uField, uvOf(mA + vec2(uLat0.x, 0.0))).r
               - texture(uField, uvOf(mA - vec2(uLat0.x, 0.0))).r) / (2.0 * uLat0.x);
    float rowsE = smst(-26.0, 0.0, min(mA.y - uFrontC.x, uFrontC.y - mA.y));
    float earth = smst(1.2, 3.0, abs(gxE) * EXAGC) * rowsE;
    if (earth > 0.5) {
      gl_Position = vec4(2.0, 2.0, 2.0, 1.0);
      vUv = vec2(0.0); vC = vec4(0.0); vRect = vec4(0.0); vHalo = vec2(0.0);
      return;
    }
  }

  /* ==== THE DRAPE'S SECOND LOOP. Six points, every one of which waits on
     the turn and on the glyph's own height:
       0  the pen itself — the anchor, whose depth the whole quad takes
       1  a unit step along the baseline, for the run's screen direction
       2  R11 (a)'s step ACROSS, the run the tilt compresses
       3  R11 (a)'s step along the AXIS, the one run the tilt spares
       4  R34 (a)'s step along the glyph's own normal, for the body's floor
       5  the quad's own corner, on the paper
     The iterations run in order, so the two points that wait on an earlier
     point's result are laid out in the iteration that draws them: 4 after
     R11's gates have run off 0..3 (and the vertex is dropped there if the
     name is too thin, or the loop left if the name is not on the paper
     path at all), and 5 after 4 has given R34's lift. ==== */
  vec3 sh0 = vec3(0.0), shq = vec3(0.0);
  vec4 c0 = vec4(0.0), cq = vec4(0.0);
  vec2 s0 = vec2(0.0), s1 = vec2(0.0), sAcr = vec2(0.0), sAxi = vec2(0.0), sNm = vec2(0.0);
  vec2 d = vec2(1.0, 0.0), nm = vec2(0.0);
  float L = 0.0, inv = 0.0, thin = 1.0, tz = 0.0, nz = 0.0;
  bool blank = false, onPaper = false;
  for (int k = 0; k < 6 + uZero; k++) {
    vec2 pm = mA; float ph = h;
    if (k == 1) pm = mA + tA;
    else if (k == 2) pm = mA + vec2(uYaw.y, uYaw.x);
    else if (k == 3) pm = mA + vec2(uYaw.x, -uYaw.y);
    else if (k == 4) {
      d = s1 - s0; L = length(d);
      d = L > 1e-5 ? d/L : vec2(1.0, 0.0);
      /* ==== R11 (a): A NAME THINS WITH DISTANCE, AND GOES WHEN IT IS SQUASHED.
         uFlatPx is the device pixels a map unit buys with the sheet flat. Two
         unit steps are taken from the anchor, and rot(yaw) is what picks them:
         ACROSS (sin, cos), which rot(yaw) sends to (0, 1) — the sheet's own
         north-south, the run the tilt compresses and the rows pile along — and
         the AXIS (cos, -sin), sent to (1, 0), the one direction the tilt
         leaves alone. acr against flat is therefore the crowding, tilt and
         perspective together, in one number; and the word's own tangent
         against the AXIS is the squash the tilt put on this particular run,
         free of the distance. The kind's own far is the crowding it survives
         to (E2's octave, a quarter again wide); the squash is shared, a third,
         which is the item's "under about 35 % of its flat length". ==== */
      inv = uFlatPx > 1e-5 ? 1.0 / uFlatPx : 0.0;
      float acr = length(sAcr - s0) * inv;          /* the rows' own compression */
      float axi = length(sAxi - s0) * inv;          /* the one run the tilt spares */
      float fore = axi > 1e-4 ? (L * inv) / axi : 1.0;
      thin = uRelief < 0.02 ? 1.0
           : smoothstep(iF, iF * 1.25, acr) * smoothstep(0.30, 0.42, fore);
      if (uLetThin < 0.5) thin = 1.0;    /* ?letthin=0 — every name at every attitude */
      /* ==== R49: ONE NAME OF A TURNED PAIR IS INKED, NEVER BOTH.
         A horizon is named twice, at right angles (the horizon block in
         place()): the meridian set DOWN the sheet, and the same name turned to
         run ACROSS it. iT is the map y of the MERIDIAN'S OWN MIDDLE, negative
         on the meridian and positive on the turned name, and the squash is read
         THERE, once, for both of them — so the two gates are exactly g and
         1 - g on the same number and the pair crossfades on the tilt with
         their inks summing to one at every attitude and every bearing.

         WHY THE MIDDLE, AND WHY ONE POINT FOR BOTH. R11 (a)'s own gate is
         per-glyph, and down a meridian the perspective varies enormously —
         measured at the tilt cap (53 deg, west, yaw 0) the meridian run is 0.08
         of the tilt axis at row 0.5 and 0.33 at row 6.5, a factor of four down
         one sheet. Per-glyph, the head of the meridian name goes and its FOOT
         STAYS, and its foot is exactly where the turned name is set: the two
         printed through each other (measured on the 2024 sheet at the cap).
         Read once, at the word's own middle, the pair changes over as two whole
         words, which is R2 (b)'s ruling — a word is one object — applied to the
         fade instead of to the height.

         It costs one field sample and three projections, on the glyphs of eight
         names and nothing else on the sheet: iT is 0 everywhere else. ==== */
      if (twinOn) {
        float axiP = length(sPa - sP);
        float g = smoothstep(0.30, 0.42, axiP > 1e-4 ? length(sPy - sP) / axiP : 1.0);
        /* and uRelief guards it as it guards the other two: with the sheet
           flat the meridian reads and the turned name is not inked at all,
           so the fit cannot move by a pixel. */
        if (uRelief < 0.02) g = 1.0;
        thin *= iT > 0.0 ? 1.0 - g : g;
      }
      thin *= reachOf(mA);      /* R13 (b): no word where no ground is drawn */
      if (thin < 0.004) { blank = true; break; }
      /* ==== R34 (a): THE QUAD IS FOUR CORNERS OF THE PAPER.
         The glyph's cell is carried in MAP units along the baseline's own map
         tangent and its normal, and each corner is projected in its own right:
         the letter therefore grows with the zoom, foreshortens with the sheet,
         and takes the perspective of the paper it is printed on — and two
         letters printed a gap apart keep that gap at every attitude, which is
         what stops them piling under yaw. The normal is (-t.y, t.x): map +y is
         DOWN the screen, so the glyph's own up is its negative, which is the
         sign the device-pixel path's own normal carried.

         THE DEPTH IS STILL THE ANCHOR'S, NOT THE CORNER'S. Taking each corner's
         own depth is the honest picture of a plate lying on the paper, and it
         COST EVERY NAME AT THE PITCHED POSE (measured: at tilt 53, yaw 0, zoom
         1060 the plate came back with no name on it at all, TERRA INCOGNITA
         included). The quad is at the word's own height h, which R2 (b) takes as
         the HIGHEST ground under the baseline — so across the glyph's own body,
         which runs at right angles to that baseline, the ground can be level
         with it or over it, and on the flat conjecture it is exactly coplanar:
         the letter z-fights the sheet it is printed on and the sheet wins. R2's
         ruling is that a word is ONE object in the depth buffer, and that is the
         answer here too: the corner is placed on the paper and tested at the
         anchor's depth, so a hill in front still hides the name and the paper
         under it never does. ==== */
      if (uLetMap > 0.5) {
        onPaper = true;
        nm = vec2(-tA.y, tA.x);
        /* ==== R34 (a): THE LETTERFORM HAS A FLOOR, THE SPACING HAS NONE.
           A letter printed on paper foreshortens with the paper, and at the
           clamp's own limit that is not a letter any more: MEASURED at tilt 53,
           yaw 0, the glyph's own body — the run at right angles to its baseline,
           which is the direction the tilt and the perspective both compress —
           came out at 0.25 of its flat height, and R11's fades then took what
           was left, so the pitched plate carried no name at all (room-rev5/
           R34-tiltprobe-nothin.png, and the quads themselves in R34-vis-t53.png).
           The engine already refuses to let the tilt squash a name past a floor
           — that is R11 (a)'s fore gate, 0.30 to 0.42, on the BASELINE. The
           same floor is put on the BODY here: the letterform foreshortens with
           the paper the whole way down to uLetFore of its flat height and then
           holds, while the baseline, and with it every gap between the letters,
           goes on foreshortening with no floor at all — which is what stops the
           names piling under yaw (R34 d). At the desk's own pose (tilt 22) the
           body measures 0.93 and the floor does nothing whatever; ?letfore=0 is
           the pair, the paper's own arithmetic with no floor under it. ==== */
        /* ==== R66: THE LETTERFORM ON THE FALL'S PLANE.
           R34 (a) set the cell in map units about the anchor — four corners of
           the PAPER — but all four at the ANCHOR's height, so the cell was a
           horizontal patch however the ground under it ran. Under R2 (b)'s single
           level that was right: every glyph of a word stood at one height, so the
           horizontal patch WAS the word's own plane. R57's fit takes that away —
           the glyphs descend along the run — and the horizontal cell becomes a
           lie: the letters step down a line that is not their own baseline, shear
           across it and pile into each other (room-rev7/R57-devon-after.png).
           So the cell's two edges take the surface's own rise:
           · ALONG the run, the fitted line's slope, which is exactly the rise the
             glyph ORIGINS already walk — so a letter's width and the gap to its
             neighbour foreshorten by the same figure and the word reads as one
             run of type again;
           · ACROSS it, the ground's own gradient (uLetCell 2), so the cell lies
             in the surface's tangent plane: the normal is the ground's.
           THE PLAN FOOTPRINT IS UNTOUCHED — tA and nm are the map vectors they
           were and only a height is added to each. That is what paper does when
           the relief rises under it: the print goes with the paper, stretched
           along the slope exactly as the paper is and no more. On level ground
           both rises are nought and the cell is R34 (a)'s own horizontal patch,
           to the bit (measured at tilt 0: 31 px of desk noise, no name moved). */
        tz = uLetCell > 0.5 ? runSlope * dot(tA, runDir) : 0.0;
        nz = 0.0;
        /* a water name follows its own polyline, so the glyph's own body is
           not always the run's: the word's cross-slope is taken along the
           glyph's own normal. On a straight name this is nzW exactly. */
        if (rollOn) nz = nzW * dot(nm, nmR);
      }
      if (!onPaper) break;
      pm = mA + nm; ph = h + nz;
    }
    else if (k == 5) {
      float bf = max(length(sNm - s0) * inv, 1e-4);
      float lift = max(bf, uLetFore) / bf;
      float qx = iQ.x + a.x*iQ.z, qy = (iQ.y + a.y*iQ.w)*lift;
      pm = mA + tA*qx + nm*qy; ph = h + tz*qx + nz*qy;
    }
    vec3 shp = mapToSheet(pm, ph);
    vec4 cp = sheetToClip(shp);
    vec2 sp = scrOf(cp);
    if (k == 0) { sh0 = shp; c0 = cp; s0 = sp; vSheet = sh0.xy; }  /* R52: the pen's own point on the paper */
    else if (k == 1) s1 = sp;
    else if (k == 2) sAcr = sp;
    else if (k == 3) sAxi = sp;
    else if (k == 4) sNm = sp;
    else { shq = shp; cq = cp; }
  }
  if (blank) {
    gl_Position = vec4(2.0, 2.0, 2.0, 1.0);
    vUv = vec2(0.0); vC = vec4(0.0); vRect = vec4(0.0); vHalo = vec2(0.0);
    return;
  }
  if (onPaper) {
    /* R52: on the PAPER path the quad is four corners of the paper and a
       long name spans real ground, so the pool is taken corner by corner. */
    vSheet = shq.xy;
    gl_Position = vec4(cq.xy, (c0.z/c0.w - uLetZ)*cq.w, cq.w);
    vUv = mix(iUV.xy, iUV.zw, a);
    vC = vec4(iC.rgb, iC.a * thin * iS); vRect = iUV; vHalo = vec2(max(iH.x, 0.0), iH.y);
    return;
  }
  /* ?letup=cam holds every name upright to the CAMERA instead (the pair
     for the standing ruling, and what 8102 does when it is raised: its
     lettering is on a screen-space overlay over the drape) */
  if (uLetUp > 0.5) d = vec2(1.0, 0.0);
  vec2 nn = vec2(-d.y, d.x);
  vec2 p = s0 + d*(iQ.x + a.x*iQ.z) + nn*(iQ.y + a.y*iQ.w);
  gl_Position = vec4(vec2(p.x/uVpx.x*2.0 - 1.0, 1.0 - p.y/uVpx.y*2.0)*c0.w, c0.z - uLetZ*c0.w, c0.w);
  vUv = mix(iUV.xy, iUV.zw, a);
  vC = vec4(iC.rgb, iC.a * thin * iS); vRect = iUV; vHalo = vec2(max(iH.x, 0.0), iH.y);
}`;
  var LET_FS = `#version 300 es
precision highp float; precision highp sampler2D;
` + LAMP_FS + PAPER_FS + `
in vec2 vUv; in vec4 vC; in vec4 vRect; in vec2 vHalo;
in vec2 vSheet;
out vec4 o;
uniform sampler2D uAtlas;
uniform float uLetVis;   /* ?letvis=1 — paint the quad, not the glyph */
/* R4: the names are inked LAST, after the face and after the ground, over
   a quarter of a second. The engine always writes this (1.0 at rest), so
   there is no default to guard against — and a guard would have made the
   first instant of the fade, 0.0, mean "no fade" and flash the names on. */
uniform float uFade;
const vec3 PAPER = vec3(0.96862745,0.93725490,0.86666667);   // --paper-hi
float tap(vec2 uv){ return texture(uAtlas, clamp(uv, vRect.xy, vRect.zw)).a; }
/* LH1: the disc's taps sit in loops whose count varies per fragment, so
   they read level 0 explicitly — the atlas has no mips, and texture()'s
   implicit derivatives are undefined in non-uniform control flow. */
float tapL(vec2 uv){ return textureLod(uAtlas, clamp(uv, vRect.xy, vRect.zw), 0.0).a; }
void main(){
  if (uLetVis > 0.5) { o = vec4(0.8, 0.0, 0.0, 0.8); return; }   /* the quad itself */
  /* LH1: the halo's radius in DEVICE pixels — atlas uv over uv per pixel,
     taken here, in uniform flow, before any branch. */
  float hPx = vHalo.x / max(length(vec2(dFdx(vUv.x), dFdy(vUv.x))), 1e-7);
  float f = tap(vUv);
  float hl = 0.0;
  /* THE HALO IS DILATED HERE, NOT BAKED. The sheet sets every name with
     paint-order: stroke fill and a 2.6px paper stroke, and that stroke
     is a constant number of SCREEN pixels at every size; baking it into
     the atlas would fix it to one ratio and make it heavy on a 9px figure
     and thin on a 20px word. R34 (a): the dilation is in ATLAS uv, so on
     the paper path it grows with the type as the sheet comes closer — a
     printed halo is part of the printing, and the fifth-of-the-x-height
     rule the table is set to therefore holds at every zoom. Eight taps of the glyph's own coverage give
     the same silhouette at the right width for whatever size this
     instance is being drawn at. */
  /* ==== LH1: BUT EIGHT TAPS ARE A DILATION ONLY WHILE THE RADIUS IS
     SMALL. The halo is the union of eight copies of the glyph moved r
     along the axes and diagonals; neighbouring copies are 0.77 r apart
     and every one is at the full r, so as soon as r in device pixels is
     wider than a hairline or a serif the copies stop touching each other
     and the stroke: at the grown zoom (horizonLabel's 1.4 at 3x is 7.4
     device px, #1622,582,339) the rim came apart into eight ghosted,
     stepped outlines round each letter — Tommy's "weird white jagged
     surround". A real dilation is a max over the whole DISC. So under
     ~3 device px (every halo at the fit: 1.5 css x 1.75 = 2.6) the eight
     taps are kept exactly, and above it the disc is filled in rings no
     more than 2 px apart, each ring's taps no more than ~1.4 px apart
     round it, in multiples of eight in phase with the eight — so the
     disc always contains them and the rim only ever fills in. Counts are
     capped (6 rings, 64 a ring) and the walk stops once the paper is
     solid; it costs taps only on the few glyphs drawn large. ==== */
  if (vHalo.x > 0.0 && hPx <= 3.0) {
    hl = max(max(max(tap(vUv + vec2( vHalo.x, 0.0)), tap(vUv + vec2(-vHalo.x, 0.0))),
                 max(tap(vUv + vec2(0.0,  vHalo.y)), tap(vUv + vec2(0.0, -vHalo.y)))),
             max(max(tap(vUv + vHalo*0.71), tap(vUv - vHalo*0.71)),
                 max(tap(vUv + vec2(vHalo.x, -vHalo.y)*0.71), tap(vUv + vec2(-vHalo.x, vHalo.y)*0.71))));
    hl = max(hl, f);
  } else if (vHalo.x > 0.0) {
    hl = f;
    int R = int(min(ceil(hPx / 2.0), 6.0));
    for (int k = 1; k <= 6; k++) {
      if (k > R || hl > 0.995) break;
      float rk = float(k) / float(R);
      int N = int(min(8.0 * ceil(6.2831853 * hPx * rk / 11.2), 64.0));
      float da = 6.2831853 / float(N);
      vec2 cs = vec2(cos(da), sin(da)), d = vec2(1.0, 0.0);
      for (int j = 0; j < 64; j++) {
        if (j >= N) break;
        hl = max(hl, tapL(vUv + vHalo * d * rk));
        d = vec2(d.x*cs.x - d.y*cs.y, d.x*cs.y + d.y*cs.x);
      }
    }
  }
  float a = max(f, hl*0.82) * vC.a * uFade;
  if (a < 0.003) discard;
  vec3 col = mix(PAPER, vC.rgb, clamp(f/max(max(f, hl*0.82), 1e-4), 0.0, 1.0));
  /* FD2 (a): and the paper it is printed on has creases in it and two
     books under it. On the ONE frame that strikes R58's print raster this
     is the identity and the lantern is left off with it — the sheet
     applies both when it samples the ink. */
  col = paperInk(col, vSheet);
  col = lampTemp(col, vSheet);   /* R52: the lantern LAST, ink and halo together; LB1: in the print too */
  o = vec4(col*a, a);
}`;

  /* ---------------- attach: the atlas, the layout, the draw -------- */
  /* R4: the one program, asked for in one place — the engine issues it
     after the first frame and attach() finds it made (see marks.js). */
  function programs(H) {
    return H.prog(H.gl, LET_VS_HEAD + H.chunks.FIELD + H.chunks.FRONT + H.chunks.XFORM +
                  ('const float EXAGC = ' + H.EXAG + '.0;') + LET_VS_BODY, LET_FS, 'letter');
  }

  function attach(H) {
    var gl = H.gl, S = H.S;
    var P = programs(H);
    var tex = gl.createTexture(), vao = gl.createVertexArray(), ibuf = gl.createBuffer();
    var qb = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, qb);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([0,0, 1,0, 0,1, 1,1]), gl.STATIC_DRAW);
    gl.bindVertexArray(vao);
    gl.bindBuffer(gl.ARRAY_BUFFER, qb);
    gl.enableVertexAttribArray(0); gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);
    gl.bindBuffer(gl.ARRAY_BUFFER, ibuf);
    var STRIDE = 24 * 4;                      /* R11 (a): + iF; R49: + iT */
    [[1,4,0],[2,4,4],[3,4,8],[4,4,12],[5,2,16],[6,4,18],[7,1,22],[9,1,23]].forEach(function (d) {
      gl.enableVertexAttribArray(d[0]);
      gl.vertexAttribPointer(d[0], d[1], gl.FLOAT, false, STRIDE, d[2] * 4);
      gl.vertexAttribDivisor(d[0], 1);
    });
    /* ==== R37 (a): THE SCREEN TEST'S FADE RIDES ITS OWN BUFFER.
       One float a glyph, in a buffer of its own, so a fade in flight
       re-uploads 1.5 kB and not the 35 kB the layout packs — and the
       layout, which runs on the zoom and not on the frame, is untouched
       by it. ==== */
    var sbuf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, sbuf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([1]), gl.DYNAMIC_DRAW);
    gl.enableVertexAttribArray(8);
    gl.vertexAttribPointer(8, 1, gl.FLOAT, false, 0, 0);
    gl.vertexAttribDivisor(8, 1);
    gl.bindVertexArray(null);

    var A = null, labels = [], buf = new Float32Array(0), nGlyph = 0, dirty = true;
    var lastKey = '', gaps = { n: 0, box: new Float32Array(64), rot: new Float32Array(32) };
    var stats = { atlas: [], glyphs: 0, layoutMs: 0, band: 0, bytes: 0, advMaxErr: 0 };
    /* R37 (a): the placed names' own patches of paper, their screen boxes,
       what each one WANTS to be (1 kept, 0 covered) and where its fade has
       got to; and the per-glyph buffer the fade is drawn from. */
    var LAB = [], LBOX = [], LORD = [], LWANT = [], LCUR = [], LKEY = '',
        sarr = new Float32Array(0), sdirty = true, moving = false, lastT = 0;
    var coll = { labels: 0, shown: 0, faded: 0, ms: 0, maxMs: 0, quads: 0, on: LETCOLL > 0 };

    function setBand(band) {
      if (A && A.band === band) return false;
      A = buildAtlas(band, charsetOf(labels));
      /* ON THE ATLAS'S OWN UNIT, and the field put back on 0 after. This
         runs from inside the ground's frame (engine.js, LP4's one layout for
         the four passes), after the field is bound: an upload on whatever
         unit was active put the glyphs where the ground reads its heights,
         and the frame a zoom crossed a band flashed flat brown with giant
         letters on it (Tommy's frame grab, 2026-09-26). */
      gl.activeTexture(gl.TEXTURE3);
      gl.bindTexture(gl.TEXTURE_2D, tex);
      gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false);
      gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
      var t0 = performance.now();
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, A.cv);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
      gl.activeTexture(gl.TEXTURE0); gl.bindTexture(gl.TEXTURE_2D, S.texField);
      A.upMs = +(performance.now() - t0).toFixed(2);
      /* the baked advance table against the face actually loaded */
      var e = 0;
      for (var c in A.adv.r) if (ADV && ADV.r && ADV.r[c] !== undefined)
        e = Math.max(e, Math.abs(A.adv.r[c] - ADV.r[c]));
      stats.advMaxErr = +e.toFixed(4);
      if (!ADV) ADV = A.adv;               // no baked table: use what was measured
      stats.atlas.push({ band: band, w: A.W, h: A.H, glyphs: A.n,
                         buildMs: A.ms, uploadMs: A.upMs, bytes: A.bytes });
      stats.band = band; stats.bytes = A.bytes;
      return true;
    }

    /* the band: within a factor of two of the size the glyph is drawn at.
       R34 (a): the drawn size now grows with the zoom, so the band has to
       climb with it — `grow` is the multiplier the layout is using. A step
       costs a re-cut and a re-upload, so there is HYSTERESIS on the way back
       down (a band is kept until the want falls under 0.72 of it), which
       stops a zoom that hovers on a boundary from re-cutting every frame. */
    function bandFor(grow) {
      var want = 16 * S.dpr * (S.cam ? S.cam.k : 1) * grow;
      if (A && A.band >= want && want > A.band * 0.72 && A.band <= BAND_MAX) return A.band;
      for (var i = 0; i < BANDS.length; i++)
        if (BANDS[i] >= want || BANDS[i] >= BAND_MAX) return Math.min(BANDS[i], BAND_MAX);
      return BAND_MAX;
    }

    /* ==== R34 (a): THE TOP BAND IS WARMED IN IDLE, NOT IN THE GESTURE.
       The first cut at a new band is expensive and every cut after it is
       not: measured on this record, band 64's first rasterisation was 9.1 ms
       of fillText against 0.4-0.6 ms for every later one, because the face
       has to be hinted and cached at that size once. Left alone, that 9 ms
       lands in the middle of the first zoom past the band boundary and costs
       a frame (layoutMs 13.7 with the upload). So the face is hinted at the
       top band ONCE, in an idle callback after the first layout, on a canvas
       that is thrown away: no texture, no upload, no draw, and the zoom's own
       cut is then the cheap one. Skipped entirely where there is no idle
       callback to ask for. ==== */
    var warmed = false;
    function warmTop() {
      if (warmed || !A || A.band >= BAND_MAX || !labels.length) return;
      warmed = true;
      stats.warmMs = -1;
      var go = typeof requestIdleCallback === 'function'
             ? function (f) { requestIdleCallback(f, { timeout: 3000 }); }
             : function (f) { setTimeout(f, 400); };
      go(function () {
        var t0 = performance.now();
        var cv = document.createElement('canvas');
        cv.width = 8; cv.height = 8;
        var cx = cv.getContext('2d');
        var need = charsetOf(labels);
        for (var st = 0; st < 2; st++) {
          cx.font = (st ? 'italic ' : '') + BAND_MAX +
                    'px "Sorts Mill Goudy LN", "Sorts Mill Goudy", Georgia, serif';
          var CS = st ? need.i : need.r;
          for (var i = 0; i < CS.length; i++) cx.measureText(CS[i]);
          cx.fillText(CS, -999, -999);
        }
        stats.warmMs = +(performance.now() - t0).toFixed(2);
      });
    }

    function setLabels(list) {
      labels = (list || []).map(function (L) {
        var p = L.p, cum = [0], tot = 0;
        for (var i = 0; i + 3 < p.length; i += 2) {
          tot += Math.hypot(p[i + 2] - p[i], p[i + 3] - p[i + 1]); cum.push(tot);
        }
        return { k: L.k, t: L.t, p: p, cum: cum, len: tot, off: L.off, anchor: L.anchor,
                 run: L.run || null, wall: L.wall === undefined ? null : L.wall,       /* R34 (b) */
                 turn: L.turn || 0 };                                                  /* R49 */
      });
      dirty = true;
    }
    function onCurve(L, s) {
      var p = L.p, cum = L.cum, n = cum.length;
      var i = 0;
      if (s <= 0) i = 0;
      else if (s >= L.len) i = n - 2;
      else { while (i < n - 2 && cum[i + 1] < s) i++; }
      var a0 = cum[i], d = (cum[i + 1] - a0) || 1;
      var t = (s - a0) / d;
      var x0 = p[i * 2], y0 = p[i * 2 + 1], x1 = p[i * 2 + 2], y1 = p[i * 2 + 3];
      return [x0 + (x1 - x0) * t, y0 + (y1 - y0) * t, (x1 - x0) / d, (y1 - y0) / d];
    }

    /* THE LAYOUT RUNS ONCE PER ZOOM, NOT ONCE PER FRAME. Every name is a
       fixed size on screen and a fixed curve on the ground, so how much
       GROUND a name covers changes only when the scale does. A pan moves
       no letter relative to its own coast, so a drag does not lay out a
       single glyph — which is why the per-move cost is E2's unchanged. */
    /* ==== R34 (b): A NAME STAYS WHILE ITS FEATURE IS ON THE SHEET.
       Every name was set at the MIDDLE of its own construction line — the
       middle of the sound, the middle of the trip, the meridian of the
       fault — and nothing ever asked whether that middle was in the window.
       At the fit the whole plate is in the window so it always was; at zoom
       530 the window is 530 x 137 map units out of a 2120-unit plate and
       NOT ONE of the 58 labels has its middle inside it (measured on this
       record, REPORT-room-R34). So the plate carried spot heights and
       station tents and no name at all — Tommy's "the names ... are GONE".
       The layout runs once per zoom and knows the window, so the name is
       slid ALONG ITS OWN FEATURE to the middle of the part of it that is in
       view: `run` (set at emit time) says how far it may go — the whole
       polyline for a water name in its sound, a signed map-unit range for a
       fault, an ocean, a range, a trip, a calm, and nothing at all for a
       peak or a year, which are points and do not move. Nothing is DROPPED
       here: the lod bands add names as the reader comes closer, they never
       take one away. Off at the fit (z <= 1.01), so the flat picture cannot
       move and a pan at the fit still lays out no glyph. ==== */
    /* ==== R34 (b): "IN THE WINDOW" IS A QUESTION ABOUT THE GLASS.
       The first two cuts at this asked it of a RECT IN PLAN — `S.view`,
       which is what the window covers with the sheet flat. Raise the sheet
       and that is the wrong question in both directions at once: ground
       beyond the rect comes into view down the screen, and ground inside it
       goes behind a ridge or off the top. Measured at tilt 53 yaw 40 zoom
       1060, the plan rect judged TERRA INCOGNITA to be outside the window
       when it was plainly on the glass, slid it into the far band, and
       R11 (a)'s crowding fade took it (room-rev5/R34-tp-norun-y40.png is
       that view with the follow off, the name set and legible).
       So the question is put to the PROJECTION, through the same VP the
       shaders use and over the same relief (`day.js`'s toScreen, R6's round
       trip): is this point of the baseline inside the portal, inset by the
       name's own cap height, AND on the plate? It is exact at every
       attitude by construction, it costs one matrix multiply and one field
       sample a point, and the layout that asks it runs once per zoom, per
       pan and per attitude — never per frame. Where there is no `day` to
       ask (it attaches after the first frame) the plan rect is the
       fallback, which is what the flat sheet would have answered anyway. */
    var VW = { x0: 0, y0: 0, x1: 0, y1: 0, on: false, l: 0, t: 0, r: 0, b: 0, scr: null };
    function inWin(x, y, pad, padPx) {
      if (x < VW.x0 + pad || x > VW.x1 - pad || y < VW.y0 + pad || y > VW.y1 - pad) return false;
      if (!VW.scr) return true;
      var q = VW.scr(x, y);
      return !!q && q[0] >= VW.l + padPx && q[0] <= VW.r - padPx &&
                    q[1] >= VW.t + padPx && q[1] <= VW.b - padPx;
    }
    function runIn(L, s, wMap, pad, padPx) {
      var a = onCurve(L, s), b = onCurve(L, s + wMap * 0.5), c = onCurve(L, s + wMap);
      return inWin(a[0], a[1], pad, padPx) && inWin(b[0], b[1], pad, padPx) &&
             inWin(c[0], c[1], pad, padPx);
    }
    /* R71 (c): how far a word set at s stands OFF the plateau — the most any
       of five points of its baseline lies east of the front's fence or
       outside the rows' — and 0 when it is wholly on it. */
    function offPlate(L, sq, wMap, fx, fn, fs) {
      var m = 0, i, q;
      for (i = 0; i <= 4; i++) {
        q = onCurve(L, sq + wMap * (i / 4));
        m = Math.max(m, q[0] - fx, fn - q[1], q[1] - fs);
      }
      return m;
    }
    function visSpan(L, pad, padPx) {
      var lo = -1, hi = -1;
      for (var i = 0; i <= 48; i++) {
        var sq = L.len * (i / 48), q = onCurve(L, sq);
        if (inWin(q[0], q[1], pad, padPx)) { if (lo < 0) lo = sq; hi = sq; }
      }
      return lo < 0 ? null : [lo, hi];
    }

    function layout(force) {
      var z = H.fitWidth() / S.view.w;
      /* R18: the atlas term is the CUT's own gen, not its band — the same
         band can be two different packings, and this layout caches cuts. */
      /* R34 (a): the growth, and (b) the window the names are slid into.
         The window is part of the key ONLY when the sheet is bigger than it
         — at the fit a pan changes nothing and lays out no glyph, which is
         E2's per-move cost kept. Quantised to a fiftieth of the window so a
         drag re-lays about fifty times across a screen, not every frame. */
      var grow = z < 1 ? 1 : z > GROW_CAP ? GROW_CAP : z;
      /* LN1 (2026-10-08): AND NOT WHILE SHE SAILS. Tommy, of the rowboat: "it says the Precambrian sea above you,
         and it just keeps re-rendering as you go along, so the words stay above you". Under way the camera follows
         her, so every name slid with her; the names stand at their features' middles until she stops */
      var RV = window.Room && Room.voyage, sailing = !!(RV && RV.state && RV.state().state === 'sailing');
      VW.on = LETMAP && LETRUN && z > 1.01 && !sailing;
      function mkKey() {
        var k = z.toFixed(5) + '|' + S.dpr + '|' + (sailing ? 's' : '') + '|' + (S.cam ? S.cam.k.toFixed(3) : '') +
                '|' + (A ? A.gen : 0);
        if (VW.on) k += '|' + Math.round(S.view.x / (S.view.w * 0.02)) +
                        ',' + Math.round(S.view.y / (S.view.h * 0.02)) +
                        ',' + (S.tilt || 0).toFixed(2) + ',' + (S.yaw || 0).toFixed(2);
        return k;
      }
      var dbg = DBG ? [] : null, dbgN = [0, 0], dbgF = DBG ? [] : null;   /* R71 */
      var key = mkKey();
      if (!force && !dirty && key === lastKey) return false;
      var t0 = performance.now();
      if (setBand(bandFor(grow))) key = mkKey();
      lastKey = key; dirty = false;
      var dpr = S.dpr, mapPerCss = S.view.w / S.portal.w;
      /* R34 (a): MAP UNITS PER CSS PIXEL OF THE TYPE TABLE. At the fit this
         is mapPerCss, so a name covers exactly the ground it covered before;
         at zoom z it is mapPerCss x min(z, GROW_CAP), so the name keeps the
         same ground (and so grows with the sheet) until the cap, after which
         it gives ground back and holds its size on screen. */
      var u = mapPerCss * grow;
      /* the PLATE is the outer bound in map units — nothing is lettered off
         the paper — and the PORTAL, in css pixels, is the glass. */
      var PL = S.fld && S.fld.plate;
      /* R71: the front's own geography in map units, which the layout needs
         and the worker's placer never had — the FENCE a name of the record
         stays west of (the front column's west edge, e0), and the FOOT of
         the cut (e1 plus the run the ground falls over east of it, R35's
         `margin.scarp`) that TERRA INCOGNITA starts at. Null where there is
         no front to fence: a wholly surveyed plate, or one with none. */
      var FR = (S.fld && S.fld.front && !S.fld.front.all && !S.fld.front.none)
        ? S.fld.front : null;
      var FRSC = (S.fld && S.fld.margin && S.fld.margin.scarp) || 0;
      /* AND ONLY ON THE RAISED SHEET. Flat, the relief is nought: no name
         hangs over a drop and none is written down a cut, and R34's rule
         that the flat picture cannot move holds — at the fit R71 unset
         TERRA INCOGNITA (664 units of word against 553 of downs) and the
         spot figure at the front, which is a loss for nothing. The bar is
         R11 (c)'s own: a quarter of a radian of tilt, the same line the
         skyline raise turns on at. */
      var RAISED = Math.sin(S.tilt || 0) > 0.25;
      VW.x0 = PL ? PL.x : S.view.x; VW.x1 = PL ? PL.x + PL.w : S.view.x + S.view.w;
      VW.y0 = PL ? PL.y : S.view.y; VW.y1 = PL ? PL.y + PL.h : S.view.y + S.view.h;
      var Pv = S.portalScr || S.portal;         /* MP2: the plate on the glass */
      VW.l = Pv.l; VW.t = Pv.t;
      VW.r = Pv.l + Pv.w; VW.b = Pv.t + Pv.h;
      /* AU3: and the GLASS's window is the portal as the camera draws it.
         The css rect is the window only while the camera is square over
         the sheet; in the pitched pose (R17, the default) its foot is drawn
         some 50 css px higher, so a name judged against the rect hung off
         the bottom of the map and the scissor cut it. The scissor's own box
         (RoomWater.portalBox, device px, y up) is the honest one. */
      var gb = (window.RoomWater && RoomWater.portalBox && S.cv) ? RoomWater.portalBox(S) : null;
      if (gb && S.dpr > 0) {
        var gt = (S.cv.height - gb.y - gb.h) / S.dpr;
        VW.l = Math.max(VW.l, gb.x / S.dpr); VW.r = Math.min(VW.r, (gb.x + gb.w) / S.dpr);
        VW.t = Math.max(VW.t, gt); VW.b = Math.min(VW.b, gt + gb.h / S.dpr);
      }
      VW.scr = (S.day && S.day.toScreen) ? S.day.toScreen : null;
      if (!VW.scr) {                      /* no projection yet: the plan rect */
        VW.x0 = Math.max(VW.x0, S.view.x); VW.x1 = Math.min(VW.x1, S.view.x + S.view.w);
        VW.y0 = Math.max(VW.y0, S.view.y); VW.y1 = Math.min(VW.y1, S.view.y + S.view.h);
      }
      var lz = Math.log(z) / Math.LN2;
      var fMid = H.smoothstep(H.LEV_B - 0.5, H.LEV_B + 0.5, lz);
      var fNear = H.smoothstep(H.LEV_C - 0.5, H.LEV_C + 0.5, lz);
      var need = 0, i, j;
      for (i = 0; i < labels.length; i++) need += labels[i].t.length;
      if (buf.length < need * 24) buf = new Float32Array(need * 24 + 512);   /* R49: + iT */
      var o = 0, ng = 0, gn = 0;
      LAB.length = 0;
      for (i = 0; i < labels.length; i++) {
        var L = labels[i], K = KIND[L.k];
        if (!K) continue;
        var al = K.a * (K.lod === 'mid' ? fMid : K.lod === 'near' ? fNear : K.lod === 'wide' ? (1 - fNear) : 1);
        if (al < 0.004) continue;
        /* ==== R9 (c): A FIGURE WHOSE BOX LEAVES THE PLATE IS NOT SET.
           The contour figures are placed ONCE, in map units, against the
           lettering's own reservations; the neat line is not one of them,
           so a level whose longest line runs along the plate's south edge
           set its "20" half over the edge and the window's scissor cut it
           (room-rev2/s-zoom400-flat.png, bottom). A survey sheet does not
           print half a figure: it moves it or it leaves the level unset.
           The test belongs HERE and not in the worker, because how much
           GROUND a figure covers is the zoom's business — it is a fixed
           size on screen — and this layout already runs once per zoom and
           already knows `mapPerCss`. One rect compare per label. */

        /* R34 (a): the quad is in MAP units now (`u` per css px of the type
           table), not device pixels. `?letmap=0` puts it back. */
        var un = LETMAP ? u : dpr;
        var scale = K.size * un / A.band;
        var qw = A.cw * scale, qh = A.ch * scale;
        var ox = -A.originX * scale, oy = -A.originY * scale;
        var offX = (L.off ? L.off[0] : 0) * un, offY = (L.off ? L.off[1] : 0) * un;
        var pen = LETMAP ? u : mapPerCss;      /* map units per css px of pen */
        var track = K.track * K.size;
        var total = 0, advs = [];
        for (j = 0; j < L.t.length; j++) {
          var aw = advOf(L.t[j], K.it) * K.size + (j < L.t.length - 1 ? track : 0);
          advs.push(aw); total += aw;
        }
        var anch = (L.anchor === undefined ? 0.5 : L.anchor);
        var wMap = total * pen;
        /* ==== TI1 (2026-09-29): TERRA INCOGNITA FITS ITS PAPER, FLAT TOO.
           Launch QA: at the fit on a 1280x720 window the name read "TERRA
           INCOGN" and at 1440x900 "TERRA INCOGNIT" — the unsurveyed strip
           narrows as the year runs on (late September it is three months),
           and only the RAISED sheet had R71 (b)'s fit test, so the flat one
           ran the word off the east neat line and the plate's edge cut it.
           Tommy's rule is R71 (b)'s: "I don't think it should bother writing
           it unless it can properly fit it onto the map." So the word is
           set SMALLER to fit between its wall and the east end of its paper
           (a cap height clear of each), down to TERRA_FIT_MIN of its size,
           and below that it is not set at this zoom. FLAT ONLY: raised, the
           shrunk word was set again where R71 (b) had refused it and stood
           against the scarp and the surveyor at the front (checked at
           #1900,420,1400,45,-20), so the raised sheet keeps R71 (b) as it
           was — the whole word or none. ==== */
        /* ==== AU4 (2026-10-02): AND THE WORD STARTS EAST OF THE SURVEY
           PARTY. The wall was e0, the front column's WEST edge, and the
           party (marks.js 'camp', a level and a figure) stands on today's
           cell at e1 — so wherever the word was shrunk to fit and centred
           (1536, 1366, 1100 and 1028 wide) its T was under the tripod (the
           audit's A5). The flat wall is now e1 plus the party's own width:
           two of the word's cap heights, which is ~32 css px of type and the
           figure is 22 wide. ==== */
        var terraWall = null;
        if (L.k === 'terra' && FR && !RAISED) {
          var tPad = K.size * 0.8 * pen;
          var tWall = L.wall !== undefined && L.wall !== null ? L.wall : FR.e0;
          var tWallP = Math.max(tWall, FR.e1 + 2 * tPad);
          /* where east of the party is too narrow for the word even at
             TERRA_FIT_MIN (1100 wide, late in the year), it keeps its old
             wall and is set a line LOWER instead, under the party's feet:
             the word is the paper's and still has its room, and the T no
             longer stands where the tripod does */
          var tMin = TERRA_FIT_MIN;
          if ((FR.east - tWallP - tPad) / wMap >= TERRA_FIT_MIN) tWall = tWallP;
          else {
            /* clear of the front's own edge (it wanders east of e0 up to e1)
               and down a line, and allowed a tenth smaller to make the room */
            tWall = Math.max(tWall, FR.e1 + 0.5 * tPad);
            offY += K.size * 1.9 * un; tMin = TERRA_FIT_MIN - 0.1;
          }
          terraWall = tWall;
          var tRoom = FR.east - tWall - tPad;
          if (wMap > tRoom) {
            var tf = tRoom / wMap;
            if (tf < tMin) continue;
            scale *= tf; qw *= tf; qh *= tf; ox *= tf; oy *= tf; pen *= tf; wMap *= tf;
          }
        }
        var s = L.len * 0.5 - wMap * anch;
        /* R34 (b): slide the name along its feature into the window */
        var pad = K.size * 0.8 * pen;          /* its own cap height, in map units */
        var padPx = K.size * 0.8 * grow;       /* and in css pixels, on the glass */
        if (DBG && L.run) dbgN[0]++;
        if (DBG && VW.on && L.run && L.len > wMap) dbgN[1]++;
        if (VW.on && L.run && L.len > wMap && !runIn(L, s, wMap, pad, padPx)) {
          var sp = visSpan(L, pad, padPx);
          if (DBG) dbg.push({ t: L.t, len: +L.len.toFixed(1), w: +wMap.toFixed(1),
                              pad: +pad.toFixed(1), s0: +s.toFixed(1),
                              sp: sp ? [+sp[0].toFixed(1), +sp[1].toFixed(1)] : null });
          /* a fifth of the name's own length of its feature in the window
             is enough to letter it: the name is then centred on what IS in
             view, which is where a surveyor would have set it. */
          if (sp && sp[1] - sp[0] > wMap * 0.2) {
            var rlo, rhi, hm = L.len * 0.5 - wMap * anch;
            if (L.run === 'curve') { rlo = 0; rhi = L.len - wMap; }
            else { rlo = hm + Math.min(L.run[0], L.run[1]); rhi = hm + Math.max(L.run[0], L.run[1]); }
            rlo = Math.max(0, Math.min(rlo, L.len - wMap));
            rhi = Math.max(0, Math.min(rhi, L.len - wMap));
            if (rhi >= rlo) s = Math.max(rlo, Math.min(rhi, (sp[0] + sp[1]) * 0.5 - wMap * 0.5));
          }
        }
        /* THE WALL (terra): the word's first glyph never stands west of it,
           whatever the zoom grew the word to or where the window slid it. */
        /* ==== R71 (b): AND FOR TERRA INCOGNITA THE WALL IS THE SCARP'S FOOT.
           Tommy, at #1268,396,1837,50,75: "you can see a weird thing where
           Terra Incognita kind of writes into the land. I don't think it
           should bother writing it unless it can properly fit it onto the
           map." The wall the placer set is `front.e0`, the front column's
           WEST edge — which is the head of the five-day fade, with the cut
           face itself standing at e1 and the ground falling over a further
           `margin.scarp` east of that. So at a zoom where the word is long
           the slide put its head at e0 and the word was written down the
           cut. The placer cannot know that margin (it is handed `front`,
           not `margin`), so the figure is finished HERE, where `S.fld` is:
           the word starts at the foot of the fall plus its own cap height,
           and if it will not stand between that foot and the east end of
           its own run it is not written at this zoom at all. ==== */
        var wallX = terraWall !== null ? terraWall : L.wall;
        if (RAISED && wallX !== null && wallX !== undefined && FR && L.k === 'terra') {
          wallX = FR.e1 + FRSC + pad;
        }
        if (wallX !== null && wallX !== undefined) {
          var wq = onCurve(L, s);
          if (wq[2] > 1e-6) { var wneed = (wallX - wq[0]) / wq[2]; if (wneed > 0) s += wneed; }
        }
        /* AU4: and, flat, the shrunk word pushed east of the party never
           runs off the east end of its paper either (TI1 sized it to fit
           between the two, so this only takes back what the push gave) */
        if (terraWall !== null) {
          var tq = onCurve(L, s);
          if (tq[2] > 1e-6) { var tover = (tq[0] + wMap) - (FR.east - K.size * 0.8 * pen); if (tover > 0) s -= tover / tq[2]; }
        }
        if (RAISED && FR && L.k === 'terra') {
          var hq = onCurve(L, s);
          if (hq[2] > 1e-6) {
            if (DBG) dbgF.push({ t: L.t, k: L.k, set: !(wallX + wMap > FR.east + 1e-6),
                                 w: +wMap.toFixed(1), wall: +wallX.toFixed(1),
                                 x0: +hq[0].toFixed(1), x1: +(hq[0] + wMap).toFixed(1),
                                 east: +FR.east.toFixed(1) });
            /* it does not fit on the unsurveyed paper: it is not set */
            if (wallX + wMap > FR.east + 1e-6) continue;
            /* and it never runs off the east end of that paper either */
            var over = (hq[0] + wMap) - FR.east;
            if (over > 0) s -= over / hq[2];
          }
        }
        /* ==== R71 (a): A NAME OF THE RECORD STAYS ON THE PLATEAU.
           Tommy, on Carcassonne at #1268,396,1837,53,79: "I don't like the
           way you can see Carcassonne ... It comes through the scrag and is
           split up ... I just don't think you should be able to see text
           through land", and the ruling: "they shouldn't go across the
           front column. It should sit wholly on the plateau, and it's
           hidden from the east."
           R2 (b) sets a word at ONE height — the highest of the nine ground
           samples under its own run — which is what keeps a name in one
           piece; a name whose run hangs east past the front therefore
           stands at PLATEAU height over the drop, and from the east it is
           read against the cut face and cut where it dips behind it. The
           worker's placer cannot refuse that: its `baselineStep` reads
           `fieldAt`, which has no scarp in it. The layout can, because it
           alone knows the zoom and therefore how much GROUND the word
           covers. So the word is slid WEST along its own run — the mirror
           of the wall step above — until every point of its baseline is
           west of the fence, and if the run it has west of the fence is
           shorter than the word, the name is NOT SET at this zoom, the way
           a glyph with no atlas rect is not set. A name already wholly west
           of the fence does not move by a pixel, and a name whose own
           feature begins east of the front (TERRA INCOGNITA, and anything
           lettered on the downs) is not fenced at all.
           THE ROOM AND THE LAW ARE NOT THE SAME LINE. The word is slid to
           e0 LESS its own cap height, which is the room a surveyor leaves;
           what it is REFUSED for is Tommy's own line — "they shouldn't go
           across the front column" — which is e0 itself. So a name that has
           run out of feature a few units short of the padded fence is still
           set where it stands, wholly west of the front (Carcassonne at
           #1268,396,1837,50,75 ends 20 units west of e0 and stays), and
           only a name whose ink actually enters the front column goes. ==== */
        /* ==== R71 (c): AND THE PLATEAU HAS A NORTH AND A SOUTH EDGE. Tommy,
           at #1101,431,2120,52,78 in a wide window: "how do you explain
           this, then?" — Carcassonne standing off the drop again. It was
           not the front: its lake is a COLUMN of three cells, so the name
           runs steeply up the column, and at a window whose scale makes the
           word longer than the column it ran off the record's top row and
           hung over the mould's northern fall, which from that bearing is
           the same picture as the front. The fence knew the front alone.
           So a name that starts on the plateau (west of e0, inside the
           rows) is kept on the plateau on every side: east of it the front
           column, north and south of it the rows, less the 26 units the
           mould already falls over inside them. The word is slid along its
           own run to the place that stands least off the plateau — nearest
           its own place when several do — and refused only when its ink
           still leaves the plateau there. ==== */
        if (RAISED && FR && L.k !== 'terra') {
          var pf0 = onCurve(L, 0);
          if (pf0[0] < FR.e0 && pf0[1] >= FR.c0 && pf0[1] <= FR.c1) {
            var padY = Math.max(pad, 26), fex0, fi, fq0, fq2, fx0 = 0, fx1 = 0, off0;
            fq0 = onCurve(L, s); fq2 = onCurve(L, s + wMap); fx0 = fq0[0]; fx1 = fq2[0];
            off0 = offPlate(L, s, wMap, FR.e0 - pad, FR.c0 + padY, FR.c1 - padY);
            if (off0 > 0) {
              var flo, fhi, hm2 = L.len * 0.5 - wMap * anch;
              if (L.run === 'curve') { flo = 0; fhi = L.len - wMap; }
              else if (L.run) { flo = hm2 + Math.min(L.run[0], L.run[1]); fhi = hm2 + Math.max(L.run[0], L.run[1]); }
              else { flo = s; fhi = s; }          /* a point name has nowhere to go */
              flo = Math.max(0, Math.min(flo, L.len - wMap)); fhi = Math.max(0, Math.min(fhi, L.len - wMap));
              var best = s, bestOff = off0, NF = 24, cand, o2;
              for (fi = 0; fi <= NF; fi++) {
                cand = flo + (fhi - flo) * (fi / NF);
                o2 = offPlate(L, cand, wMap, FR.e0 - pad, FR.c0 + padY, FR.c1 - padY);
                if (o2 < bestOff - 1e-6 ||
                    (Math.abs(o2 - bestOff) <= 1e-6 && Math.abs(cand - s) < Math.abs(best - s))) { best = cand; bestOff = o2; }
              }
              s = best;
            }
            /* THE ROOM AND THE LAW ARE NOT THE SAME LINE: the word is slid
               to the padded edges, and refused only where its ink actually
               leaves the plateau — the front column, or the rows. */
            fex0 = offPlate(L, s, wMap, FR.e0, FR.c0, FR.c1);
            fq0 = onCurve(L, s); fq2 = onCurve(L, s + wMap);
            if (DBG && (off0 > 0 || L.t.replace(/ /g, '').indexOf('arcasso') >= 0)) {
              dbgF.push({ t: L.t, k: L.k, set: fex0 <= 0, w: +wMap.toFixed(1), off0: +off0.toFixed(1),
                          fence: +(FR.e0 - pad).toFixed(1), rows: [+(FR.c0 + padY).toFixed(1), +(FR.c1 - padY).toFixed(1)],
                          was: [+fx0.toFixed(1), +fx1.toFixed(1)],
                          x0: +fq0[0].toFixed(1), x1: +fq2[0].toFixed(1),
                          y0: +fq0[1].toFixed(1), y1: +fq2[1].toFixed(1),
                          off: +fex0.toFixed(1), e0: +FR.e0.toFixed(1) });
            }
            if (fex0 > 0) continue;              /* its ink leaves the plateau: not set */
          }
        }
        /* ==== AU3 (2026-10-02): AND NO NAME IS CUT BY THE WINDOW'S EDGE.
           The audit (A4) found big names cut mid-word by the edge of the
           map: "OPUS 5.5 · 22 SEP 20", "RRA INCOGNITA", "ago di Roma",
           "PRECAMBRIAN SE". R34 (b) slides a name into view only when its
           middle is out, and a point name (a peak, a year) never moves, so
           a name with its middle in and its end out was set and the
           scissor cut it. A printed sheet never prints half a word: the
           whole word, five points of its baseline a half cap height inside
           the glass and the plate, is moved inward along its own feature
           to the nearest place it fits — without crossing the walls and
           fences above — or it is not set at this view. ==== */
        if (VW.on && !K.mark) {
          var hp = pad * 0.5, hpx = padPx * 0.5;
          var wholeIn = function (sq) {
            for (var wi = 0; wi <= 4; wi++) {
              var wq = onCurve(L, sq + offX + wMap * (wi / 4));
              /* the GLASS only: the margin west and east of the plate is
                 drawn ground too (the year before, the downs), and a name
                 standing on it in view is not cut by anything */
              if (VW.scr) {
                var wsq = VW.scr(wq[0], wq[1]);
                if (!wsq || wsq[0] < VW.l + hpx || wsq[0] > VW.r - hpx ||
                    wsq[1] < VW.t + hpx || wsq[1] > VW.b - hpx) return false;
                /* and, flat, on the paper: the window is a rect THERE, and
                   only its bounding box on the glass (the drape and the
                   camera's pitch lean its sides). Raised, a point's paper
                   position carries its height, so the glass is the test. */
                /* AU10: and, raised, not behind a hill. The word is
                   depth-tested, so a hill in front of it cut it mid-word
                   ('Carcassonne' half sunk at #1182,431,1400,60,180). The
                   pick's own ray through the point's pixel must land on the
                   point: if it lands nearer, ground stands in front of it. */
                if (RAISED && S.day && S.day.probe) {
                  var wpk = S.day.probe(wsq[0], wsq[1]);
                  if (wpk && Math.hypot(wpk.x - wq[0], wpk.y - wq[1]) > Math.max(hp * 2, 3)) return false;
                }
                if (!RAISED && S.day && S.day.toSheet) {
                  var wsp = S.day.toSheet(wq[0], wq[1]), P0 = S.portal;
                  if (wsp[0] < P0.l + hp / pen || wsp[0] > P0.l + P0.w - hp / pen ||
                      wsp[1] < P0.t + hp / pen || wsp[1] > P0.t + P0.h - hp / pen) return false;
                }
              } else if (!inWin(wq[0], wq[1], hp, hpx)) return false;
            }
            return true;
          };
          var lawful = function (sq) {
            if (RAISED && FR && L.k === 'terra') {
              var a0 = onCurve(L, sq);
              if (a0[0] < wallX - 1e-6 || a0[0] + wMap > FR.east + 1e-6) return false;
            } else if (wallX !== null && wallX !== undefined && onCurve(L, sq)[0] < wallX - 1e-6) return false;
            if (RAISED && FR && L.k !== 'terra') {
              var pz = onCurve(L, 0);
              if (pz[0] < FR.e0 && pz[1] >= FR.c0 && pz[1] <= FR.c1 &&
                  offPlate(L, sq, wMap, FR.e0, FR.c0, FR.c1) > 0) return false;
            }
            return true;
          };
          if (!wholeIn(s)) {
            var found = false;
            if (L.run && L.len > wMap) {
              var alo, ahi, hm3 = L.len * 0.5 - wMap * anch;
              if (L.run === 'curve') { alo = 0; ahi = L.len - wMap; }
              else { alo = hm3 + Math.min(L.run[0], L.run[1]); ahi = hm3 + Math.max(L.run[0], L.run[1]); }
              alo = Math.max(0, Math.min(alo, L.len - wMap));
              ahi = Math.max(0, Math.min(ahi, L.len - wMap));
              var bestS = 0, bestD = Infinity, NS = 48;
              for (var si = 0; si <= NS; si++) {
                var cs = alo + (ahi - alo) * (si / NS);
                var dd = Math.abs(cs - s);
                if (dd < bestD && wholeIn(cs) && lawful(cs)) { bestD = dd; bestS = cs; }
              }
              if (bestD < Infinity) { s = bestS; found = true; }
            }
            if (!found) continue;              /* it does not fit in view: not set */
          }
        }
        /* THE WORD'S OWN BASELINE — the run the glyphs actually occupy on
           the construction line, start and vector, in map units. One height
           is taken along it in the shader (R2 b), and the word is turned
           about its middle when it would read backwards (R2 c). */
        var sBase = s;
        var wA = onCurve(L, s), wB = onCurve(L, s + wMap);
        var wx = wA[0], wy = wA[1], wdx = wB[0] - wA[0], wdy = wB[1] - wA[1];
        var rect, c = K.rgb;
        var hx = K.halo > 0 ? (K.halo * A.band / K.size) / A.W : 0;
        var hy = K.halo > 0 ? (K.halo * A.band / K.size) / A.H : 0;
        var midPt = null, gi0 = ng;
        for (j = 0; j < L.t.length; j++) {
          rect = A.rects[(K.it ? 'i' : 'r') + L.t[j]];
          if (!rect) { s += advs[j] * pen; continue; }
          var q = onCurve(L, s);
          if (DBG && dbgF.length && dbgF[dbgF.length - 1].t === L.t) { (dbgF[dbgF.length - 1].pts = dbgF[dbgF.length - 1].pts || []).push([+q[0].toFixed(1), +q[1].toFixed(1)]); }
          if (L.t[j] !== ' ') {
            buf[o] = q[0]; buf[o+1] = q[1]; buf[o+2] = q[2]; buf[o+3] = q[3];
            buf[o+4] = ox + offX; buf[o+5] = oy + offY; buf[o+6] = qw; buf[o+7] = qh;
            buf[o+8] = rect[0]; buf[o+9] = rect[1]; buf[o+10] = rect[2]; buf[o+11] = rect[3];
            buf[o+12] = c[0]; buf[o+13] = c[1]; buf[o+14] = c[2]; buf[o+15] = al;
            buf[o+16] = K.inPlate ? -1 : hx; buf[o+17] = hy;
            buf[o+18] = wx; buf[o+19] = wy; buf[o+20] = wdx; buf[o+21] = wdy;
            buf[o+22] = (K.far || 0) * FAR;         /* R11 (a) */
            buf[o+23] = L.turn || 0;                /* R49 */
            o += 24; ng++;
          }
          if (j === (L.t.length >> 1)) midPt = q;
          s += advs[j] * pen;
        }
        /* the gap the contour figure CUTS in its own line */
        if (K.cut && midPt && gn < 16) {
          var cw2 = (total * 0.5 + 2.4) * pen;
          var chh2 = (K.size * 0.60) * pen;
          var oyc = (LETMAP ? offY / un : offY / dpr) - K.size * 0.32;   /* css px */
          var cxp = midPt[0] + (-midPt[3]) * oyc * pen;
          var cyp = midPt[1] + (midPt[2]) * oyc * pen;
          gaps.box[gn * 4] = cxp; gaps.box[gn * 4 + 1] = cyp;
          gaps.box[gn * 4 + 2] = cw2; gaps.box[gn * 4 + 3] = chh2;
          gaps.rot[gn * 2] = midPt[2]; gaps.rot[gn * 2 + 1] = midPt[3];
          gn++;
        }
        /* ==== R37 (a): AND THE WORD'S OWN GROUND IS KEPT FOR THE SCREEN
           TEST. The sheet's reservations (`reserve`/`hits`, in the worker)
           are in SHEET units and guarantee the plan; what the reader sees
           under a tilt or a bearing is the PROJECTION of those boxes, and
           two boxes that are clear on the paper can cover each other on the
           glass. So every name that was actually set leaves its own patch
           of paper here — the run the glyphs occupy and the cell's height
           across it, in map units — and screenPass() projects the four
           corners and settles the overlaps in device pixels. Nothing is
           measured twice: this is the arithmetic the layout has already
           done. ==== */
        if (ng > gi0 && !K.mark) {
          /* THE WORD IS ON ITS OWN CURVE, AND THE CHORD IS NOT THE WORD.
             Carcassonne is set round the head of its bay: the straight run
             from its first pen to its last passes clean through the spot
             height "90" forty pixels off the arc, and a box on that chord
             faded the figure at the FIT (measured, 151 device px). So the
             word's own run is walked and kept in pieces — a piece to about
             every five letters, four at most, which is the item's ~200
             quads over the whole sheet — and each piece is projected and
             tested in its own right. */
          /* A NAME ON A STRAIGHT LINE IS ONE PIECE. The spot figures, the
             contour figures and the meridians are all set on a two-point
             construction line, so every piece of them would be collinear
             and one quad is exact — which is most of the sheet's names and
             most of this pass's cost. */
          var nK = L.p.length <= 4 ? 1 : Math.min(4, Math.max(1, Math.ceil(L.t.length / 5)));
          var seg = [];
          /* the word's INK starts at the pen plus its own offset along the
             baseline — the spot height's figure stands five screen pixels
             east of its dot (styles.css .sv-spot-height), and a box that
             began at the dot reached five pixels west of the figure,
             which is exactly the way Carcassonne lies. */
          for (var sq = 0; sq < nK; sq++) {
            var pa = onCurve(L, sBase + offX + wMap * (sq / nK));
            var pb = onCurve(L, sBase + offX + wMap * ((sq + 1) / nK));
            var ddx = pb[0] - pa[0], ddy = pb[1] - pa[1];
            var tl = Math.hypot(ddx, ddy) || 1;
            seg.push([pa[0], pa[1], ddx, ddy, -ddy / tl, ddx / tl]);
          }
          /* ==== AND THE BOX IS THE INK'S, NOT THE CELL'S. The atlas cell
             is nearly twice the type's own size — it carries the face's
             whole ascender-to-descender run and the margin the halo is
             dilated into — and a box that size faded the spot height "90"
             at the fit with eighteen clear pixels of paper between it and
             Carcassonne (measured: the cell 26.2 css px on a 13.5 px
             name). So the box is the FACE's: the cap height over the
             baseline, the descender under it, and the halo's own radius
             on each, in the same css pixels the type table is set in. */
          var asc = (K.size * 0.72 + (K.halo || 0)) * un;
          var dsc = (K.size * 0.22 + (K.halo || 0)) * un;
          LAB.push({ g0: gi0, g1: ng, far: K.far || 0, size: K.size, t: L.t, k: L.k,
                     seg: seg, n0: offY - asc, n1: offY + dsc,
                     /* R49: the turned name is kept off the screen test */
                     turn: L.turn || 0 });
        }
      }
      warmTop();
      gaps.n = gn; nGlyph = ng; stats.glyphs = ng;
      if (DBG) { stats.dbg = dbg; stats.win = [VW.x0, VW.y0, VW.x1, VW.y1];
                 stats.dbgN = dbgN.concat([VW.on ? 1 : 0, labels.length]);
                 stats.fence = dbgF; }                 /* R71 */
      stats.grow = +grow.toFixed(3); stats.growCap = GROW_CAP; stats.mapQuad = LETMAP;
      gl.bindBuffer(gl.ARRAY_BUFFER, ibuf);
      gl.bufferData(gl.ARRAY_BUFFER, buf.subarray(0, o), gl.DYNAMIC_DRAW);
      LKEY = '';                              /* R37 (a): the boxes moved */
      sdirty = true;
      stats.layoutMs = +(performance.now() - t0).toFixed(3);
      return true;
    }

    /* =================================================================
       R37 (a). NAMES THAT NEVER COLLIDE — THE TEST ON THE GLASS.
       Tommy: "Is there any way to make sure that text on the map never
       collides?"

       The sheet's own test is exact and it is not enough. `place()` lays
       every name once on the FLAT sheet and reserves its box there, so at
       plan nothing can overprint by construction. Under a tilt or a
       bearing the projection is not the flat one — the rows foreshorten,
       R11 (c) raises a word onto its skyline, R34 grows the type with the
       zoom — and a box that is clear on the paper can cover another name
       on the GLASS. R11 (a) thinned the far band, which is a different
       thing: it takes names away by distance, it does not sort out two
       names that happen to land on each other.

       So the guarantee is made where the reader is: every placed name's
       patch of paper is projected to device pixels, the names are walked
       in the sheet's own priority (the seas and TERRA first at far 0.13,
       then the reaches, the fjords and the ranges at 0.19, then the rest),
       the first of any pair is kept and the later one is FADED — over
       LETCOLL ms, so a name goes out and comes back like ink drying and
       never pops. A name off the glass neither fades nor blocks.

       It runs when the ATTITUDE OR THE VIEW MOVES, not every frame: the
       key below is the view, the tilt, the bearing and the layout's own
       generation, quantised the way the layout quantises its own. At the
       plan the projection IS the flat one, so the screen test finds
       exactly what the sheet test found and nothing fades (measured:
       0 faded at the fit, diff 0).
       ================================================================= */
    /* ==== R37 (a): THE NAME'S BOX IS THE NAME'S BOX, NOT ITS SHADOW.
       A name set DOWN the sheet (Devon Sound in its sound, Carcassonne on
       its trip) projects to a quad lying at an angle on the glass, and the
       upright rectangle around that quad is very much bigger than the
       name: at the fit it covered the spot height "90" two hundred pixels
       away and faded it (measured, 151 device px against the before).
       So the four corners are kept and the test below is the separating
       axis, with the upright box only as the cheap reject.

       THE CELL IS TALLER THAN THE LETTER. The atlas cell carries the
       face's whole ascender-to-descender run and a margin for the halo,
       and a name set under another would fail on that whitespace and not
       on its ink; the inset is taken in the name's OWN axes, before the
       projection, so it is the ink's box at every attitude. ==== */
    function boxOf(L, sc) {
      /* NEIGHBOURING PIECES SHARE THEIR CORNERS, so a name of four pieces
         is ten projections and not sixteen — the projection is a field
         sample and a matrix, and it is the whole cost of this pass. */
      var out = [], h0 = L.n0, h1 = L.n1, n2 = L.seg.length, i2;
      var lo0 = null, lo1 = null, g = L.seg[0];
      lo0 = sc(g[0] + g[4] * h0, g[1] + g[5] * h0);
      lo1 = sc(g[0] + g[4] * h1, g[1] + g[5] * h1);
      if (!lo0 || !lo1) return null;
      for (i2 = 0; i2 < n2; i2++) {
        g = L.seg[i2];
        var bx = g[0] + g[2], by = g[1] + g[3];
        var hi0 = sc(bx + g[4] * h0, by + g[5] * h0);
        var hi1 = sc(bx + g[4] * h1, by + g[5] * h1);
        if (!hi0 || !hi1) return null;
        var q = [lo0[0], lo0[1], hi0[0], hi0[1], hi1[0], hi1[1], lo1[0], lo1[1]];
        q.push(Math.min(q[0], q[2], q[4], q[6]), Math.min(q[1], q[3], q[5], q[7]),
               Math.max(q[0], q[2], q[4], q[6]), Math.max(q[1], q[3], q[5], q[7]));
        out.push(q);
        lo0 = hi0; lo1 = hi1;
      }
      return out.length ? out : null;
    }
    /* the separating axis over two convex quads — the four edge normals,
       and nothing else is needed for two convex four-sided figures */
    function apart(A1, B1) {
      if (A1[10] < B1[8] || A1[8] > B1[10] || A1[11] < B1[9] || A1[9] > B1[11]) return true;
      for (var s2 = 0; s2 < 2; s2++) {
        var Q = s2 ? B1 : A1, R = s2 ? A1 : B1;
        for (var e = 0; e < 4; e++) {
          var i0 = e * 2, i1 = ((e + 1) & 3) * 2;
          var nx2 = -(Q[i1 + 1] - Q[i0 + 1]), ny2 = Q[i1] - Q[i0];
          var qlo = Infinity, qhi = -Infinity, rlo = Infinity, rhi = -Infinity, k2, d2;
          for (k2 = 0; k2 < 4; k2++) {
            d2 = Q[k2 * 2] * nx2 + Q[k2 * 2 + 1] * ny2;
            if (d2 < qlo) qlo = d2; if (d2 > qhi) qhi = d2;
            d2 = R[k2 * 2] * nx2 + R[k2 * 2 + 1] * ny2;
            if (d2 < rlo) rlo = d2; if (d2 > rhi) rhi = d2;
          }
          if (qhi <= rlo || rhi <= qlo) return true;
        }
      }
      return false;
    }
    function screenPass() {
      var sc = (S.day && S.day.toScreen) ? S.day.toScreen : null;
      if (!coll.on || !LETMAP || !LAB.length || !sc) {
        if (!LAB.length) { coll.labels = 0; coll.shown = 0; coll.faded = 0; }
        return;
      }
      var t0 = performance.now();
      var key = S.view.x.toFixed(2) + ',' + S.view.y.toFixed(2) + ',' + S.view.w.toFixed(3) +
                ',' + (S.tilt || 0).toFixed(4) + ',' + (S.yaw || 0).toFixed(4) +
                ',' + S.dpr + ',' + nGlyph + ',' + LAB.length;
      if (key !== LKEY) {
        LKEY = key;
        var i, j, n = LAB.length;
        LBOX.length = n; LORD.length = 0;
        var Pv2 = S.portalScr || S.portal;  /* MP2 */
        var pl = Pv2.l, pt = Pv2.t, pr = pl + Pv2.w, pb = pt + Pv2.h, nq = 0;
        /* ==== R49: A TURNED NAME IS NOT IN THE WAY, AND NOTHING IS IN ITS.
           A horizon's name is set twice, at right angles, and WHICH of the
           two is inked is settled in the shader, on R11 (a)'s own measure of
           the meridian's squash (LET_VS_BODY). This pass cannot see that
           number — its projection is the day picker's, not the ground
           shader's, and measured against it the two disagreed by half
           (the shader inked no turned name at tilt 40 west while a JS
           reading of the same point said it should, and the box of a name
           with no ink in it put the 2026 year figure out — 461 device px,
           R49-off-w-t40.png against the first cut of R49-after-w-t40.png).
           So the turned name is kept OFF THE GLASS ALTOGETHER for this
           pass, exactly as a name outside the window is: it neither fades
           nor stands in anything's way. Its PARTNER keeps the ground it
           held in R36, so the sheet's collisions are the sheet's — at every
           attitude the picture is R36's with the turned name's ink added
           and nothing of R36's taken away (the diffs in REPORT-room-R49).
           What it buys instead of a reservation is the ladder: the row was
           tested against the marks and the names at placement, so the
           ground it turns onto was clear on the paper. ==== */
        for (i = 0; i < n; i++) {
          /* a name wholly off the glass is neither faded nor in the way */
          var b = LAB[i].turn > 0 ? null : boxOf(LAB[i], sc);
          if (b) {
            nq += b.length;
            var ux0 = Infinity, uy0 = Infinity, ux1 = -Infinity, uy1 = -Infinity;
            for (var bi = 0; bi < b.length; bi++) {
              if (b[bi][8] < ux0) ux0 = b[bi][8];
              if (b[bi][9] < uy0) uy0 = b[bi][9];
              if (b[bi][10] > ux1) ux1 = b[bi][10];
              if (b[bi][11] > uy1) uy1 = b[bi][11];
            }
            if (ux1 < pl || ux0 > pr || uy1 < pt || uy0 > pb) b = null;
          }
          LBOX[i] = b;
          if (b) LORD.push(i);
        }
        /* THE SHEET'S OWN PRIORITY, and nothing invented: `far` is the
           order R11 (a) already set the kinds in — the seas, the horizons
           and TERRA, then the reaches and the ranges, then the rest — with
           the bigger type first inside a band and the placement's own
           order under that, so the walk is the same every frame. */
        LORD.sort(function (p, q) {
          var A1 = LAB[p], B1 = LAB[q];
          return A1.far - B1.far || B1.size - A1.size || p - q;
        });
        var kept = [], keptOf = [], blocker = [];
        for (i = 0; i < n; i++) { LWANT[i] = 0; blocker[i] = null; }
        var shown = 0;
        for (i = 0; i < LORD.length; i++) {
          var k = LORD[i], bb = LBOX[k], clear = true;
          for (var bj = 0; bj < bb.length && clear; bj++)
            for (j = 0; j < kept.length; j++)
              if (!apart(bb[bj], kept[j])) { clear = false; blocker[k] = keptOf[j]; break; }
          if (clear) {
            for (j = 0; j < bb.length; j++) { kept.push(bb[j]); keptOf.push(k); }
            LWANT[k] = 1; shown++;
          }
        }
        /* a name off the glass keeps whatever it had: it is not faded */
        for (i = 0; i < n; i++) if (!LBOX[i]) LWANT[i] = 1;
        coll.labels = n; coll.shown = shown; coll.faded = LORD.length - shown; coll.quads = nq; coll.keptQ = kept.length;
        coll.on_ = []; coll.off_ = [];
        for (i = 0; i < LORD.length; i++) {
          var kk2 = LORD[i];
          (LWANT[kk2] ? coll.on_ : coll.off_).push(LAB[kk2].k + ':' + LAB[kk2].t +
            (LWANT[kk2] ? '' : ' <- ' + (blocker[kk2] !== null ? LAB[blocker[kk2]].t : '?')));
        }
      }
      /* THE FADE. A name that is covered goes out over LETCOLL ms and comes
         back the same way; nothing pops, and nothing is re-uploaded while
         every name is where it wants to be. */
      var now = performance.now();
      var dt = lastT ? Math.min(now - lastT, 100) : 16;
      lastT = now;
      var step = LETCOLL > 0 ? dt / LETCOLL : 1;
      var mv = false, m;
      for (m = 0; m < LAB.length; m++) {
        var want = LWANT[m] === undefined ? 1 : LWANT[m];
        var cur = LCUR[m] === undefined ? want : LCUR[m];
        if (cur !== want) {
          cur = want > cur ? Math.min(want, cur + step) : Math.max(want, cur - step);
          if (cur !== want) mv = true;
          sdirty = true;
        }
        LCUR[m] = cur;
        if (sdirty) {
          var L2 = LAB[m];
          for (var g = L2.g0; g < L2.g1 && g < nGlyph; g++) sarr[g] = cur;
        }
      }
      moving = mv;
      if (mv && S.invalidate) S.invalidate();
      coll.ms = +(performance.now() - t0).toFixed(3);
      if (coll.ms > coll.maxMs) coll.maxMs = coll.ms;
    }

    function draw() {
      if (!nGlyph) return;
      /* R37 (a): the fade's buffer is ALWAYS the length of the draw, so
         the attribute can never be read past its end — a short buffer on a
         divisor-1 attribute takes every name off the sheet. Off, it is a
         run of ones and is uploaded once. */
      if (sarr.length !== nGlyph) { sarr = new Float32Array(nGlyph); sarr.fill(1); sdirty = true; }
      screenPass();
      if (sdirty) {
        gl.bindBuffer(gl.ARRAY_BUFFER, sbuf);
        gl.bufferData(gl.ARRAY_BUFFER, sarr, gl.DYNAMIC_DRAW);
        sdirty = false;
      }
      gl.useProgram(P);
      gl.bindVertexArray(vao);
      gl.activeTexture(gl.TEXTURE3); gl.bindTexture(gl.TEXTURE_2D, tex);
      gl.uniform1i(P.u.uAtlas, 3);
      gl.activeTexture(gl.TEXTURE0); gl.bindTexture(gl.TEXTURE_2D, S.texField);
      gl.uniform1i(P.u.uField, 0);
      H.setCommon(gl, P);
      /* ==== R52: THE ONE LAMP. RoomObjects.lampSheet(S) is the single place
         the lantern's pool is worked out (R42), so the name is lit by the
         same lamp as the paper under it. One 4f + one 1f per frame. ==== */
      if (P.u.uLampS) {
        var lsh = (root.RoomObjects && root.RoomObjects.lampSheet)
          ? root.RoomObjects.lampSheet(S) : [0, 0, 1, 1, 0];
        gl.uniform4f(P.u.uLampS, lsh[0], lsh[1], lsh[2], lsh[3]);
        gl.uniform1f(P.u.uLampN, lsh[4]);
      }
      gl.uniform2f(P.u.uVpx, S.cv.width / (S.capK || 1), S.cv.height / (S.capK || 1));   /* PK1 */
      gl.uniform1f(P.u.uLetUp, H.upToCamera ? 1 : 0);
      if (P.u.uLetMap) gl.uniform1f(P.u.uLetMap, LETMAP ? 1 : 0);   /* R34 (a) */
      if (P.u.uLetThin) gl.uniform1f(P.u.uLetThin, LETTHIN ? 1 : 0);
      if (P.u.uLetVis) gl.uniform1f(P.u.uLetVis, LETVIS ? 1 : 0);
      if (P.u.uLetFore) gl.uniform1f(P.u.uLetFore, LETFORE);
      if (P.u.uLetFall) gl.uniform1f(P.u.uLetFall, LETFALL ? 1 : 0);      /* R57 */
      if (P.u.uLetCell) gl.uniform1f(P.u.uLetCell, LETCELL);               /* R66 */
      if (P.u.uLetRoll) gl.uniform1f(P.u.uLetRoll, LETROLL);               /* R66 */
      if (P.u.uLetCut) gl.uniform1f(P.u.uLetCut, LETCUT ? 1 : 0);  /* R57 */
      gl.uniform1f(P.u.uLetZ, LETZ);
      var Pd = S.portalScr || S.portal;         /* MP2: device px on the glass */
      if (P.u.uPortal) gl.uniform4f(P.u.uPortal, Pd.l * S.dpr, Pd.t * S.dpr,
                                    Pd.w * S.dpr, Pd.h * S.dpr);
      /* R11 (a): the device pixels a map unit buys with the sheet FLAT —
         the reference the foreshortening is measured against. It is the
         window over the view, so the zoom cancels out of the ratio. */
      if (P.u.uFlatPx) gl.uniform1f(P.u.uFlatPx, S.dpr * Pd.w / S.view.w);
      if (P.u.uSkyline) gl.uniform2f(P.u.uSkyline, SKYLINE[0], SKYLINE[1]);
      if (P.u.uFade) gl.uniform1f(P.u.uFade, S.letterFade === undefined ? 1 : S.letterFade);
      gl.enable(gl.BLEND);
      gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
      /* R2 (a): A NAME SET ON THE GROUND IS BEHIND THE HILL IN FRONT OF IT.
         The letters used to be drawn with the depth test OFF, so "Devon
         Sound" and the TERRA of TERRA INCOGNITA printed straight through
         the earthen cut face standing in front of them. They are tested
         now — against the plate, the water and E7's cut face, which are
         all that is in the buffer at this point (the conjecture clears it,
         engine.js draw() step 3) — and they do NOT WRITE it, so no letter
         can take a pixel from a mark or from E6's cross, and the ordering
         of marks, pin and thread is the one it was. The bias is the one
         the glyphs always carried, a shade over the marks' own (0.00035 /
         0.0009), so a name still wins its own ground and its own symbol;
         ?letz= is the pair for it. */
      gl.enable(gl.DEPTH_TEST);
      gl.depthMask(false);
      gl.drawArraysInstanced(gl.TRIANGLE_STRIP, 0, 4, nGlyph);
      gl.depthMask(true);
      gl.disable(gl.BLEND);
      gl.bindVertexArray(null);
    }

    return { setLabels: setLabels, layout: layout, draw: draw, gaps: gaps,
             rebuildAtlas: function () { A = null; dirty = true; },
             boxes: function () { return LAB.map(function (L, i) {
               return { t: L.t, k: L.k, n0: +L.n0.toFixed(2), n1: +L.n1.toFixed(2),
                        want: LWANT[i], q: LBOX[i] }; }); },
             stats: function () { stats.coll = coll; return stats; },
             atlas: function () { return A; },
             tex: function () { return tex; },
             invalidate: function () { dirty = true; } };
  }

  LET.attach = attach;
  LET.precompile = programs;
  LET.place = place;
  LET.KIND = KIND;
  LET.BANDS = BANDS;
  LET.buildAtlas = buildAtlas;
  LET.charsetOf = charsetOf;
  LET.inkWidth = inkWidth;
  LET.setAdv = function (t) { ADV = t; };
  LET.advOf = advOf;
})(typeof self !== 'undefined' ? self : this);
